import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  IconTrendingUp as PromoIcon,
  IconRefresh as RefreshIcon,
  IconCircleCheck as ProcessIcon,
  IconEdit as EditIcon,
  IconClipboardCheck as MatchingIcon,
  IconTimeline as TimelineIcon,
  IconPlaylistX as UnmatchedIcon,
  IconWand as QuickFixIcon,
  IconDownload as DownloadIcon,
  IconAlertTriangle as WarningIcon,
  IconFileDownload as MarkupIcon,
  IconArrowsExchange as MatchedIcon,
  IconCurrencyDollar as CommissionsIcon,
  IconLoader2,
  IconX,
  IconCheck,
  IconChevronDown,
  IconSearch,
} from "@tabler/icons-react";
import api from "../lib/api";
import { API_BASE_URL } from "../env";
import PageHeader from "../components/PageHeader";
import MainCard from "../components/MainCard";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { badgeVariants } from "@/components/ui/badge-variants";
import type { VariantProps } from "class-variance-authority";

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

/** Maps local semantic keys to shared Badge variants (error → destructive). */
const CHIP_SEMANTIC_TO_BADGE: Record<
  "default" | "success" | "warning" | "error",
  BadgeVariant
> = {
  default: "secondary",
  success: "success",
  warning: "warning",
  error: "destructive",
};
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RetailerCommissionsPage } from "./RetailerCommissionsPage";
import { StoreOwnershipChangePanel } from "../components/StoreOwnershipChangePanel";
import { RetailerStoreUploadCard } from "../components/RetailerStoreUploadCard";
import { formatDate } from "../utils/dateFormat";
import {
  getVoucherRunActionState,
  indexRunStatuses,
  type VoucherMatchingProcess,
  type VoucherMatchingRunsResult,
  type VoucherRunStatusIndex,
} from "../features/voucher-matching/run-status";
import {
  reversedQuickFixConfirmation,
  reversedQuickFixSuccess,
} from "../features/voucher-matching/quick-fix-copy";

interface PendingReward {
  i_customer: number;
  spend_30_day: number;
  value: number;
  distributor_i_customer?: number;
  distributor_name?: string | null;
  latest_spend_check_date: string | null;
  latest_created_at: string | null;
  days_since_spend_check: number | null;
  days_since_created: number | null;
  reward_sources: string | null;
  spend_requirement?: number | null;
  expires_at?: string | null;
  status?: string | null;
  billing_failure_reason?: string | null;
}

interface FailedReward {
  id: number;
  i_customer: number;
  msisdn: string | null;
  msisdn_dma: string | null;
  msisdn_churned: string | null;
  sim_serial_number: string;
  id_number: string;
  first_name: string;
  last_name: string;
  email: string | null;
  spar_uuid_failed_error: string | null;
  created_at: string;
}

interface RomOption {
  id: number;
  name: string;
  level_1_id: number | null;
  dc_name: string | null;
  contact_first_name: string | null;
  contact_last_name: string | null;
  contact_phone_number: string | null;
  contact_email_address: string | null;
}

interface StoreResult {
  id: number;
  name: string;
  external_id: string | null;
  cyclepay_terminal_id: string | null;
  level_1_id: number | null;
  level_2_id: number | null;
  distributor_i_customer: number;
  contact_first_name: string | null;
  contact_last_name: string | null;
  contact_phone_number: string | null;
  contact_email_address: string | null;
  address_line_1: string | null;
  city: string | null;
  province_id: number | null;
  updated_at: string | null;
  rom_id: number | null;
  rom_name: string | null;
  rom_contact_first_name: string | null;
  rom_contact_last_name: string | null;
  rom_contact_phone_number: string | null;
  rom_contact_email: string | null;
  rom_level_1_id: number | null;
  dc_id: number | null;
  dc_name: string | null;
}

type VoucherRecord = Record<string, any>;

interface VoucherMatchingSummary {
  range: {
    start: string;
    end: string;
    startDateTime: string;
    endDateTime: string;
  };
  totals: {
    total: number;
    matched: number;
    unmatched: number;
    markupMatched: number;
    markupUnmatched: number;
    confirmedUnmatched: number;
    confirmedMarkupIssues: number;
    authorisedPending: number;
  };
  stateBreakdown: Array<{ state: string | null; count: number }>;
  markupBreakdown: Array<{
    state: string | null;
    markup_state: string | null;
    markup_status: string | null;
    count: number;
  }>;
  matchingBreakdown: Array<{
    state: string | null;
    markup_state: string | null;
    matched_status: string | null;
    count: number;
  }>;
  dailyStats: Array<{
    day: string;
    total: number;
    matched: number;
    unmatched: number;
    markupMatched: number;
    markupUnmatched: number;
    latestMatchedAt: string | null;
  }>;
  alerts: {
    missingMatchDays: string[];
    missingMarkupDays: string[];
    cronStatus: "ok" | "warning" | "error";
    cronMessage: string;
    latestMatchedAt: string | null;
    latestVoucherCreatedAt: string | null;
  };
}

const SECTION_ORDER = [
  "promo-engine",
  "spar-rewards",
  "rom-management",
  "store-ownership-change",
  "voucher-matching",
  "incentive-reconciliation",
  "retailer-commissions",
  "expired-rewards",
] as const;
type SectionKey = (typeof SECTION_ORDER)[number];
const DEFAULT_SECTION: SectionKey = "promo-engine";

const isValidSection = (value?: string | null): value is SectionKey => {
  return !!value && SECTION_ORDER.includes(value as SectionKey);
};

const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getInitialMatchingRange = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const end = new Date(today);
  end.setDate(end.getDate() - 1);

  // Ensure the end date does not precede the start date (e.g. on the first day of the month)
  if (end < start) {
    start.setTime(end.getTime());
  }

  return {
    start: formatDateForInput(start),
    end: formatDateForInput(end),
  };
};

const getMonthDateRange = (yearMonth: string) => {
  const [year, month] = yearMonth.split("-").map(Number);
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0); // Last day of the month

  // Cap end date at yesterday since matching data is only processed up to yesterday
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // If end date would be today or in the future, cap it at yesterday
  if (end >= today) {
    end.setTime(yesterday.getTime());
  }

  // Ensure the end date does not precede the start date (e.g. on the first day of the month)
  if (end < start) {
    start.setTime(end.getTime());
  }

  return {
    start: formatDateForInput(start),
    end: formatDateForInput(end),
  };
};

const formatDateTimeDisplay = (
  value?: string | null,
  options?: Intl.DateTimeFormatOptions,
) => {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(undefined, options);
  } catch {
    return value;
  }
};

const formatDateDisplay = (value?: string | null) => {
  if (!value) return "—";
  return formatDateTimeDisplay(value, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatTimeAgo = (days: number | null | undefined): string => {
  if (days === null || days === undefined) return "Never";
  if (days < 0) return "Never";
  if (days === 0) return "Today";
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "1 week ago";
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 60) return "1 month ago";
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} year${Math.floor(days / 365) !== 1 ? "s" : ""} ago`;
};

const formatTimeAgoFromDate = (dateString?: string | null): string => {
  if (!dateString) return "-";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMs < 0) return "in the future";
    if (diffSeconds < 60) return "just now";
    if (diffMinutes < 60)
      return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    if (diffDays === 1) return "1 day ago";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return "1 week ago";
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 60) return "1 month ago";
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) !== 1 ? "s" : ""} ago`;
  } catch {
    return "-";
  }
};

interface SparMobilePageProps {
  basePath?: string;
  sectionName?: string;
}

export function SparMobilePage({
  basePath,
  sectionName,
}: SparMobilePageProps = {}) {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Detect base path from location if not provided
  const detectedBasePath =
    basePath ||
    (location.pathname.startsWith("/promo-data")
      ? "/promo-data"
      : "/spar-mobile");
  const isPromoDataRoute = detectedBasePath === "/promo-data";
  const promoDataSection = sectionName || "rewards-engine";

  // Determine the current section: prioritize sectionName prop, then section from params, then extract from URL
  let resolvedSection: string | undefined = sectionName;
  if (!resolvedSection) {
    resolvedSection = section;
  }
  if (
    !resolvedSection &&
    (detectedBasePath === "/spar-mobile" ||
      location.pathname.startsWith("/spar-mobile"))
  ) {
    // Extract section from URL path like /spar-mobile/spar-rewards
    const pathParts = location.pathname.split("/").filter(Boolean);
    const sectionIndex = pathParts.indexOf("spar-mobile");
    if (sectionIndex >= 0 && pathParts[sectionIndex + 1]) {
      resolvedSection = pathParts[sectionIndex + 1];
    }
  }

  // For promo-data route, map "rewards-engine" URL to "promo-engine" internally
  // When on promo-data route, always use promo-engine section internally
  const currentSection: SectionKey = isPromoDataRoute
    ? "promo-engine"
    : isValidSection(resolvedSection)
      ? resolvedSection
      : DEFAULT_SECTION;

  useEffect(() => {
    // Only redirect if we're using dynamic routes (with :section param)
    // If sectionName prop is provided, we're on a static route and shouldn't redirect
    if (sectionName) {
      // Static route with sectionName prop - no redirect needed
      return;
    }

    if (isPromoDataRoute && section !== promoDataSection) {
      navigate(`${detectedBasePath}/${promoDataSection}`, { replace: true });
    } else if (
      !isPromoDataRoute &&
      !isValidSection(section) &&
      !resolvedSection
    ) {
      navigate(`${detectedBasePath}/${DEFAULT_SECTION}`, { replace: true });
    }
  }, [
    section,
    sectionName,
    navigate,
    detectedBasePath,
    isPromoDataRoute,
    promoDataSection,
    resolvedSection,
  ]);

  // Promo Engine state
  const [pendingRewards, setPendingRewards] = useState<PendingReward[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState<number | null>(null);
  const [processingAll, setProcessingAll] = useState(false);
  const [processProgress, setProcessProgress] = useState({
    current: 0,
    total: 0,
  });
  const [success, setSuccess] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("pending"); // 'all', 'pending', 'processing', 'allocated', 'failed', 'expired'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [pagination, setPagination] = useState<{
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null>(null);

  // SPAR Rewards state
  const [failedRewards, setFailedRewards] = useState<FailedReward[]>([]);
  const [loadingFailed, setLoadingFailed] = useState(false);
  const [errorFailed, setErrorFailed] = useState("");
  const [processingFailed, setProcessingFailed] = useState<number | null>(null);
  const [processingAllFailed, setProcessingAllFailed] = useState(false);
  const [processProgressFailed, setProcessProgressFailed] = useState({
    current: 0,
    total: 0,
  });
  const [successFailed, setSuccessFailed] = useState("");
  const [errorTypeFilter, setErrorTypeFilter] = useState<string>("all");
  const [editingEmail, setEditingEmail] = useState<{
    id: number;
    currentEmail: string;
  } | null>(null);
  const [newEmail, setNewEmail] = useState("");

  // ROM management state
  const [storeSearchTerm, setStoreSearchTerm] = useState("");
  const [storeHasSearched, setStoreHasSearched] = useState(false);
  const [storeLoading, setStoreLoading] = useState(false);
  const [storeError, setStoreError] = useState("");
  const [storeSuccess, setStoreSuccess] = useState("");
  const [stores, setStores] = useState<StoreResult[]>([]);
  const [romOptions, setRomOptions] = useState<RomOption[]>([]);
  const [romOptionsLoading, setRomOptionsLoading] = useState(false);
  const [selectedRomMap, setSelectedRomMap] = useState<
    Record<number, number | "">
  >({});
  const [updatingStores, setUpdatingStores] = useState<Record<number, boolean>>(
    {},
  );
  const [storeSuggestions, setStoreSuggestions] = useState<StoreResult[]>([]);
  const [selectedStores, setSelectedStores] = useState<StoreResult[]>([]);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestStoreSearchRef = useRef(0);
  const [notFoundCodes, setNotFoundCodes] = useState<string[]>([]);

  // Incentive Reconciliation state
  const [reconciliationDate, setReconciliationDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [reconciliationData, setReconciliationData] = useState<any>(null);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  const [reconciliationError, setReconciliationError] = useState("");

  // Monthly summary state
  const [monthlySummary, setMonthlySummary] = useState<any>(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState("");
  const [selectedMonth, setSelectedMonth] = useState<string>(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`,
  );
  const [viewMode, setViewMode] = useState<"monthly" | "daily">("monthly");
  const lastLoadedMonthRef = useRef<string | null>(null);
  const reconciliationCalculatedRef = useRef<string | null>(null);

  // Calculation state
  const [calculating, setCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState("");
  const [calculationSuccess, setCalculationSuccess] = useState("");
  const [initialCalculationDone, setInitialCalculationDone] = useState(false);

  // Allocation state
  const [allocating, setAllocating] = useState(false);
  const [allocationError, setAllocationError] = useState("");
  const [allocationSuccess, setAllocationSuccess] = useState("");

  // Voucher matching state
  const [matchingMonth, setMatchingMonth] = useState<string>(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`,
  );
  const [matchingSummary, setMatchingSummary] =
    useState<VoucherMatchingSummary | null>(null);
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [matchingError, setMatchingError] = useState("");
  const [matchingSuccess, setMatchingSuccess] = useState("");
  const [unmatchedLoading, setUnmatchedLoading] = useState(false);
  const [unmatchedError, setUnmatchedError] = useState("");
  const [unmatchedVouchers, setUnmatchedVouchers] = useState<VoucherRecord[]>(
    [],
  );
  const [unmatchedLimit, setUnmatchedLimit] = useState(50);
  const [unmatchedStateFilter, setUnmatchedStateFilter] =
    useState<string>("CONFIRMED");
  const [unmatchedMatchedFilter, setUnmatchedMatchedFilter] =
    useState<string>("UNMATCHED");
  const [unmatchedMarkupFilter, setUnmatchedMarkupFilter] =
    useState<string>("ANY");
  const [onlyProblematic, setOnlyProblematic] = useState(true);
  const [unmatchedMeta, setUnmatchedMeta] = useState<{
    count: number;
    limit: number;
    range?: { start: string; end: string };
  } | null>(null);

  // Electrum reconciliation process state
  const [processStatuses, setProcessStatuses] =
    useState<VoucherRunStatusIndex>({});
  const [processStatusAvailability, setProcessStatusAvailability] = useState<
    "loading" | "available" | "unavailable"
  >("loading");
  const [processStatusError, setProcessStatusError] = useState("");
  const processStatusRequestRef = useRef(0);
  const [runningProcess, setRunningProcess] = useState<{
    type: VoucherMatchingProcess;
    date: string;
  } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: VoucherMatchingProcess | "";
    date: string;
  }>({ open: false, type: "", date: "" });
  const lastLoadedMatchingMonthRef = useRef<string | null>(null);
  const [fixingReversed, setFixingReversed] = useState(false);
  const [fixingAuthorised, setFixingAuthorised] = useState(false);

  // Expired rewards state
  const [expiredRewards, setExpiredRewards] = useState<
    Array<{
      expire_date: string;
      spend_requirement: number;
      count: number;
      total_value: number;
      already_written_back: boolean;
    }>
  >([]);
  const [expiredRewardsLoading, setExpiredRewardsLoading] = useState(false);
  const [expiredRewardsError, setExpiredRewardsError] = useState("");
  const [expiredRewardsSuccess, setExpiredRewardsSuccess] = useState("");
  const [expiredRewardsSelectedMonth, setExpiredRewardsSelectedMonth] =
    useState<string>(
      `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`,
    );
  const [writingBack, setWritingBack] = useState<string | null>(null);
  const lastLoadedExpiredRewardsMonthRef = useRef<string | null>(null);
  const [
    expiredRewardsInitialCalculationDone,
    setExpiredRewardsInitialCalculationDone,
  ] = useState(false);
  const expiredRewardsCalculatedRef = useRef<string | null>(null);

  // Expired Rewards Monthly History
  const [expiredRewardsViewMode, setExpiredRewardsViewMode] = useState<
    "list" | "monthly"
  >("monthly");
  const [expiredRewardsMonthlySummary, setExpiredRewardsMonthlySummary] =
    useState<any>(null);
  const [expiredRewardsMonthlyLoading, setExpiredRewardsMonthlyLoading] =
    useState(false);
  const [expiredRewardsMonthlyError, setExpiredRewardsMonthlyError] =
    useState("");
  const [calculatingExpiredHistory, setCalculatingExpiredHistory] =
    useState(false);
  const [
    calculationExpiredHistorySuccess,
    setCalculationExpiredHistorySuccess,
  ] = useState("");
  const [calculationExpiredHistoryError, setCalculationExpiredHistoryError] =
    useState("");
  const lastLoadedExpiredMonthRef = useRef<string | null>(null);

  const loadExpiredRewards = useCallback(
    async (yearMonth?: string) => {
      const monthToLoad = yearMonth || expiredRewardsSelectedMonth;
      setExpiredRewardsLoading(true);
      setExpiredRewardsError("");
      setExpiredRewardsSuccess("");
      try {
        const [year, month] = monthToLoad.split("-").map(Number);
        const res = await api.get(
          `/spar-mobile/expired-rewards?year=${year}&month=${month}`,
        );
        setExpiredRewards(res.data?.data || []);
        lastLoadedExpiredRewardsMonthRef.current = monthToLoad;
      } catch (err: any) {
        console.error("Failed to load expired rewards:", err);
        setExpiredRewardsError(
          err.response?.data?.message || "Failed to load expired rewards",
        );
        setExpiredRewards([]);
      } finally {
        setExpiredRewardsLoading(false);
      }
    },
    [expiredRewardsSelectedMonth],
  );

  const loadExpiredRewardsMonthly = useCallback(
    async (yearMonth?: string) => {
      const monthToLoad = yearMonth || expiredRewardsSelectedMonth;
      setExpiredRewardsMonthlyLoading(true);
      setExpiredRewardsMonthlyError("");
      try {
        const [year, month] = monthToLoad.split("-").map(Number);
        const res = await api.get(
          `/spar-mobile/expired-rewards-monthly/${year}/${month}`,
        );
        const data = res.data?.data || null;
        setExpiredRewardsMonthlySummary(data);
        lastLoadedExpiredMonthRef.current = monthToLoad;
        lastLoadedExpiredRewardsMonthRef.current = monthToLoad;
        // Derive list view (pending-only) from monthly data so one GET serves both views
        if (data?.days) {
          const pendingDays = data.days.filter(
            (d: { status: string; count: number; total_value: number }) =>
              d.status === "pending" && (d.count > 0 || d.total_value > 0),
          );
          setExpiredRewards(
            pendingDays.map(
              (d: {
                date: string;
                spend_requirement: number;
                count: number;
                total_value: number;
              }) => ({
                expire_date: d.date,
                spend_requirement: d.spend_requirement ?? 99,
                count: d.count,
                total_value: d.total_value,
                already_written_back: false,
              }),
            ),
          );
        } else {
          setExpiredRewards([]);
        }
      } catch (err: any) {
        console.error("Failed to load expired rewards monthly history:", err);
        setExpiredRewardsMonthlyError(
          err.response?.data?.message || "Failed to load monthly history",
        );
        setExpiredRewardsMonthlySummary(null);
        setExpiredRewards([]);
      } finally {
        setExpiredRewardsMonthlyLoading(false);
      }
    },
    [expiredRewardsSelectedMonth],
  );

  const calculateExpiredRewardsHistory = useCallback(
    async (silent = false, _monthToCalculate?: string) => {
      setCalculatingExpiredHistory(true);
      if (!silent) {
        setCalculationExpiredHistoryError("");
        setCalculationExpiredHistorySuccess("");
      }
      try {
        // No body: backend uses fixed start (2025-11-01) to yesterday, same as incentive
        const res = await api.post(
          "/spar-mobile/expired-rewards/calculate-history",
          {},
        );
        if (!silent) {
          setCalculationExpiredHistorySuccess(
            res.data?.message || "History calculated successfully",
          );
        }
        if (silent) {
          setExpiredRewardsInitialCalculationDone(true);
        }
        // Always refresh monthly data so UI shows up-to-date history (after any calculate)
        lastLoadedExpiredMonthRef.current = null;
        if (expiredRewardsSelectedMonth) {
          void loadExpiredRewardsMonthly(expiredRewardsSelectedMonth);
        }
      } catch (err: any) {
        console.error("Failed to calculate expired rewards history:", err);
        if (!silent) {
          setCalculationExpiredHistoryError(
            err.response?.data?.message || "Failed to calculate history",
          );
        }
      } finally {
        setCalculatingExpiredHistory(false);
      }
    },
    [expiredRewardsSelectedMonth, loadExpiredRewardsMonthly],
  );

  const writeBackExpiredRewards = useCallback(
    async (expireDate: string, spendRequirement: number) => {
      console.log("[WRITEBACK] Called with:", {
        expireDate,
        spendRequirement,
        type: typeof spendRequirement,
      });
      setWritingBack(`${expireDate}-${spendRequirement}`);
      setExpiredRewardsError("");
      setExpiredRewardsSuccess("");
      try {
        // Extract just the date part (YYYY-MM-DD) in case it's an ISO string
        let dateToSend = expireDate;
        if (expireDate.includes("T")) {
          dateToSend = expireDate.split("T")[0];
        } else if (expireDate.includes(" ")) {
          dateToSend = expireDate.split(" ")[0];
        }

        console.log("[WRITEBACK] Sending to API:", {
          expireDate: dateToSend,
          spendRequirement,
        });
        const res = await api.post("/spar-mobile/expired-rewards/write-back", {
          expireDate: dateToSend,
          spendRequirement,
        });
        setExpiredRewardsSuccess(
          res.data?.message || "Successfully wrote back expired rewards",
        );
        // Reload monthly data (list view is derived from it); backend already updated history row
        setTimeout(() => {
          lastLoadedExpiredMonthRef.current = null;
          void loadExpiredRewardsMonthly(expiredRewardsSelectedMonth);
        }, 1000);
      } catch (err: any) {
        console.error("Failed to write back expired rewards:", err);
        setExpiredRewardsError(
          err.response?.data?.message || "Failed to write back expired rewards",
        );
      } finally {
        setWritingBack(null);
      }
    },
    [expiredRewardsSelectedMonth, loadExpiredRewardsMonthly],
  );

  const ensureRomOptions = useCallback(async (force = false) => {
    if (romOptionsLoading) {
      return;
    }
    if (!force && romOptions.length > 0) {
      return;
    }

    setRomOptionsLoading(true);
    try {
      const API_URL = API_BASE_URL;
      const response = await fetch(`${API_URL}/spar-mobile/roms`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to load ROM options");
      }

      const data = await response.json();
      setRomOptions(data.data || []);
    } catch (err: any) {
      console.error("Failed to load ROM options", err);
      setStoreError(err.message || "Failed to load ROM options");
    } finally {
      setRomOptionsLoading(false);
    }
  }, [romOptions.length, romOptionsLoading]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const loadPendingRewards = useCallback(
    async (overridePage?: number, overrideLimit?: number) => {
      setLoading(true);
      setError("");
      setSuccess("");

      try {
        const API_URL = API_BASE_URL;
        // Use the endpoint that matches the route structure
        const baseEndpoint = isPromoDataRoute
          ? `${API_URL}/promo-data/rewards-engine`
          : `${API_URL}/spar-mobile/pending-rewards`;

        const currentPage = overridePage !== undefined ? overridePage : page;
        const currentLimit =
          overrideLimit !== undefined ? overrideLimit : rowsPerPage;

        // Build query parameters
        const params = new URLSearchParams();
        if (statusFilter && statusFilter !== "all") {
          params.append("status", statusFilter);
        }
        params.append("page", String(currentPage + 1)); // Backend uses 1-indexed pages
        params.append("limit", String(currentLimit));

        const endpoint = `${baseEndpoint}?${params.toString()}`;

        console.log("[PENDING REWARDS] Fetching from:", endpoint);
        const response = await fetch(endpoint, {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            "[PENDING REWARDS] Error response:",
            response.status,
            errorText,
          );
          throw new Error(
            `Failed to fetch: ${response.status} ${response.statusText}`,
          );
        }

        const data = await response.json();
        console.log("[PENDING REWARDS] Received data:", data);
        setPendingRewards(data.data || []);
        setPagination(data.pagination || null);
        if (data.pagination) {
          const newPage = Math.max(0, (data.pagination.page || 1) - 1);
          const newLimit = data.pagination.limit || currentLimit;
          // Only update if values actually changed to avoid infinite loops
          if (newPage !== page) {
            setPage(newPage);
          }
          if (newLimit !== rowsPerPage) {
            setRowsPerPage(newLimit);
          }
        }
      } catch (err: any) {
        console.error("[PENDING REWARDS] Fetch error:", err);
        setError(err.message || "Failed to load pending rewards");
      } finally {
        setLoading(false);
      }
    },
    [isPromoDataRoute, statusFilter, page, rowsPerPage],
  );

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing rows per page
  };

  const loadFailedRewards = useCallback(async () => {
    setLoadingFailed(true);
    setErrorFailed("");

    try {
      const API_URL = API_BASE_URL;
      const response = await fetch(`${API_URL}/spar-mobile/failed-rewards`, {
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch");

      const data = await response.json();
      setFailedRewards(data.data || []);
    } catch (err: any) {
      setErrorFailed(err.message || "Failed to load failed registrations");
    } finally {
      setLoadingFailed(false);
    }
  }, []);

  const formatRands = (value: number | string) => {
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (Number.isNaN(num)) return "R 0.00";
    return `R ${num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatMB = (value: number | string) => {
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (Number.isNaN(num)) return "0 MB";
    return `${num.toLocaleString("en-US")} MB`;
  };

  const formatInteger = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return "0";
    const num = typeof value === "string" ? Number(value) : value;
    if (Number.isNaN(num)) return "0";
    return num.toLocaleString("en-US");
  };

  const formatDecimal = (
    value: number | string | null | undefined,
    digits = 2,
  ) => {
    if (value == null)
      return (0).toLocaleString("en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
    const num = typeof value === "string" ? Number(value) : value;
    if (Number.isNaN(num))
      return (0).toLocaleString("en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
    return num.toLocaleString("en-US", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  };

  const formatDateYMD = (value: string | number | Date | null | undefined) => {
    if (value == null) return "—";
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "—";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch {
      return "—";
    }
  };

  const formatDateOnly = (value?: string | null) => {
    if (!value) return "";
    if (value.includes("T")) return value.split("T")[0];
    if (value.includes(" ")) return value.split(" ")[0];
    return value;
  };

  const cronStatusToSeverity = (status: "ok" | "warning" | "error") => {
    if (status === "error") return "error";
    if (status === "warning") return "warning";
    return "success";
  };

  const getVoucherIdentifier = useCallback((voucher: VoucherRecord) => {
    const keys = [
      "voucher_code",
      "voucher_uuid",
      "voucher_number",
      "voucher",
      "code",
      "reference",
      "id",
    ];

    for (const key of keys) {
      if (voucher[key]) {
        return voucher[key];
      }
    }

    if (voucher["i_customer"]) {
      return `Customer ${voucher["i_customer"]}`;
    }

    return "Unknown";
  }, []);

  const getVoucherMsisdn = useCallback((voucher: VoucherRecord) => {
    const keys = ["msisdn", "msisdn_dma", "msisdn_churned", "customer_msisdn"];
    for (const key of keys) {
      if (voucher[key]) {
        return voucher[key];
      }
    }
    return null;
  }, []);

  const getVoucherValue = useCallback((voucher: VoucherRecord) => {
    const keys = ["amount", "value", "voucher_value"];
    for (const key of keys) {
      if (voucher[key] !== undefined && voucher[key] !== null) {
        return voucher[key];
      }
    }
    return null;
  }, []);

  const parseErrorMessage = (errorJson: string | null) => {
    if (!errorJson) return "Unknown error";
    try {
      const parsed = JSON.parse(errorJson);
      return parsed.Message || errorJson;
    } catch {
      return errorJson;
    }
  };

  const categorizeError = (errorJson: string | null): string => {
    const message = parseErrorMessage(errorJson);
    if (message.toLowerCase().includes("not old enough"))
      return "Age Verification Failed";
    if (message.toLowerCase().includes("email")) return "Invalid Email";
    if (message.toLowerCase().includes("already registered"))
      return "Already Registered";
    if (message.toLowerCase().includes("already in use")) return "Invalid Data";
    if (message.toLowerCase().includes("passport")) return "Invalid Data";
    if (message.toLowerCase().includes("invalid")) return "Invalid Data";
    return "Other Error";
  };

  const getErrorStats = () => {
    const stats: Record<string, number> = {};
    failedRewards.forEach((record) => {
      const category = categorizeError(record.spar_uuid_failed_error);
      stats[category] = (stats[category] || 0) + 1;
    });
    return stats;
  };

  const filteredFailedRewards =
    errorTypeFilter === "all"
      ? failedRewards
      : failedRewards.filter(
          (r) => categorizeError(r.spar_uuid_failed_error) === errorTypeFilter,
        );

  const downloadErrorsCSV = () => {
    // Calculate errors per day
    const errorsPerDay: Record<string, number> = {};
    filteredFailedRewards.forEach((record) => {
      const date = new Date(record.created_at).toISOString().split("T")[0]; // YYYY-MM-DD
      errorsPerDay[date] = (errorsPerDay[date] || 0) + 1;
    });

    // Sort dates descending (most recent first)
    const sortedDates = Object.keys(errorsPerDay).sort((a, b) =>
      b.localeCompare(a),
    );

    // Build summary section
    const summaryRows: string[] = [
      "Errors Summary by Date",
      "Date,Count",
      ...sortedDates.map((date) => {
        const formattedDate = new Date(date).toLocaleDateString();
        return `${formattedDate},${errorsPerDay[date]}`;
      }),
      `Total,${filteredFailedRewards.length}`,
      "", // Empty line separator
    ];

    // Build data section
    const headers = [
      "Customer ID",
      "MSISDN",
      "ID Number",
      "Email",
      "Error",
      "Created",
    ];
    const rows = filteredFailedRewards.map((record) => [
      record.i_customer.toString(),
      record.msisdn_dma || record.msisdn_churned || record.msisdn || "",
      record.id_number,
      record.email || "",
      parseErrorMessage(record.spar_uuid_failed_error),
      new Date(record.created_at).toLocaleString(),
    ]);

    const dataRows = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((cell) => {
            // Escape commas, quotes, and newlines in CSV
            const cellStr = String(cell || "");
            if (
              cellStr.includes(",") ||
              cellStr.includes('"') ||
              cellStr.includes("\n")
            ) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          })
          .join(","),
      ),
    ];

    const csvContent = [...summaryRows, ...dataRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const filterSuffix =
      errorTypeFilter === "all"
        ? "all-errors"
        : errorTypeFilter.toLowerCase().replace(/\s+/g, "-");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `spar-failed-registrations-${filterSuffix}-${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const errorStats = getErrorStats();
  const trimmedStoreSearch = storeSearchTerm.trim();
  const storeAutocompleteOptions = useMemo(() => {
    const selectedIds = new Set(selectedStores.map((store) => store.id));
    const deduped = storeSuggestions.filter(
      (store) => !selectedIds.has(store.id),
    );
    return [...selectedStores, ...deduped];
  }, [selectedStores, storeSuggestions]);

  const mergeStores = useCallback(
    (existing: StoreResult[], incoming: StoreResult[]) => {
      const map = new Map<number, StoreResult>();
      existing.forEach((store) => {
        map.set(store.id, store);
      });
      incoming.forEach((store) => {
        map.set(store.id, store);
      });
      return Array.from(map.values()).sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    },
    [],
  );

  const extractBulkCodes = useCallback((text: string) => {
    return Array.from(
      new Set(
        text
          .split(/[\s,]+/)
          .map((item) => item.trim())
          .filter((item) => item.length > 0),
      ),
    );
  }, []);

  const buildSelectionMap = useCallback((list: StoreResult[]) => {
    const map: Record<number, number | ""> = {};
    list.forEach((store) => {
      map[store.id] = store.rom_id ?? "";
    });
    return map;
  }, []);

  const performStoreSearch = useCallback(
    async (rawTerm: string) => {
      const trimmed = rawTerm.trim();

      if (trimmed.length < 2) {
        latestStoreSearchRef.current += 1;
        setStoreHasSearched(false);
        setStores(selectedStores.length > 0 ? selectedStores : []);
        setStoreSuggestions([]);
        setSelectedRomMap(
          buildSelectionMap(selectedStores.length > 0 ? selectedStores : []),
        );
        setStoreLoading(false);
        return;
      }

      latestStoreSearchRef.current += 1;
      const searchId = latestStoreSearchRef.current;

      setStoreLoading(true);
      setStoreError("");
      setStoreSuccess("");
      setNotFoundCodes([]);

      try {
        await ensureRomOptions();

        const API_URL = API_BASE_URL;
        const response = await fetch(
          `${API_URL}/spar-mobile/stores?search=${encodeURIComponent(trimmed)}`,
          {
            credentials: "include",
          },
        );

        if (!response.ok) {
          throw new Error("Failed to search stores");
        }

        const result = await response.json();
        const storeData: StoreResult[] = result.data || [];

        if (searchId !== latestStoreSearchRef.current) {
          return;
        }

        setStoreSuggestions(storeData);

        if (selectedStores.length > 0) {
          const refreshedSelection = selectedStores
            .map(
              (selected) =>
                storeData.find((store) => store.id === selected.id) || selected,
            )
            .filter(Boolean);

          setSelectedStores(refreshedSelection);
          setStores(refreshedSelection);
          setSelectedRomMap(buildSelectionMap(refreshedSelection));
        } else {
          setStores(storeData);
          setSelectedRomMap(buildSelectionMap(storeData));
        }

        setStoreHasSearched(true);
      } catch (err: any) {
        if (searchId !== latestStoreSearchRef.current) {
          return;
        }
        console.error("Failed to search stores", err);
        setStoreError(err.message || "Failed to search stores");
        setStores(selectedStores.length > 0 ? selectedStores : []);
        setStoreSuggestions([]);
        setSelectedRomMap(
          buildSelectionMap(selectedStores.length > 0 ? selectedStores : []),
        );
        setStoreHasSearched(true);
      } finally {
        if (searchId === latestStoreSearchRef.current) {
          setStoreLoading(false);
        }
      }
    },
    [ensureRomOptions, buildSelectionMap, selectedStores],
  );

  const handleRomSelectionChange = useCallback(
    (storeId: number, rom: RomOption | null) => {
      setSelectedRomMap((prev) => ({
        ...prev,
        [storeId]: rom ? rom.id : "",
      }));
    },
    [],
  );

  const handleBulkCodes = useCallback(
    async (rawCodes: string[]) => {
      const uniqueCodes = Array.from(new Set(rawCodes))
        .map((code) => code.trim())
        .filter((code) => code.length > 0)
        .slice(0, 200);

      if (uniqueCodes.length === 0) {
        setStoreError("No valid store codes detected in pasted input.");
        return;
      }

      latestStoreSearchRef.current += 1;
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      setStoreLoading(true);
      setStoreError("");
      setStoreSuccess("");
      setNotFoundCodes([]);

      try {
        await ensureRomOptions();

        const API_URL = API_BASE_URL;
        const response = await fetch(
          `${API_URL}/spar-mobile/stores/by-external-ids?codes=${encodeURIComponent(uniqueCodes.join(","))}`,
          {
            credentials: "include",
          },
        );

        if (!response.ok) {
          throw new Error("Failed to lookup stores by codes");
        }

        const result = await response.json();
        const storeData: StoreResult[] = result.data || [];

        const combinedSelection = mergeStores(selectedStores, storeData);
        setSelectedStores(combinedSelection);
        setStores(combinedSelection);
        setStoreSuggestions((prev) => mergeStores(prev, storeData));
        setSelectedRomMap(buildSelectionMap(combinedSelection));
        setStoreHasSearched(true);

        const matchedCodes = new Set<string>();
        storeData.forEach((store) => {
          [store.external_id, store.cyclepay_terminal_id]
            .filter((code): code is string => !!code)
            .forEach((code) => {
              const trimmedCode = code.trim();
              matchedCodes.add(trimmedCode);
              matchedCodes.add(trimmedCode.replace(/^0+/, ""));
            });
        });

        const unresolved = uniqueCodes.filter((code) => {
          const normalized = code.replace(/^0+/, "");
          return !matchedCodes.has(code) && !matchedCodes.has(normalized);
        });

        setNotFoundCodes(unresolved);

        if (storeData.length > 0) {
          setStoreSuccess(
            `Added ${storeData.length} store${storeData.length === 1 ? "" : "s"} from pasted list.`,
          );
          setTimeout(() => setStoreSuccess(""), 6000);
        }

        if (unresolved.length > 0) {
          console.warn("Unresolved store codes:", unresolved);
        }
      } catch (err: any) {
        console.error("Failed to lookup stores", err);
        setStoreError(err.message || "Failed to lookup stores");
      } finally {
        setStoreSearchTerm("");
        setStoreLoading(false);
      }
    },
    [ensureRomOptions, mergeStores, selectedStores, buildSelectionMap],
  );

  const handleStoreInputChange = useCallback(
    (_event: unknown, value: string, reason: string) => {
      if (reason === "reset") {
        setStoreSearchTerm(value);
        return;
      }

      if (reason === "clear") {
        if (searchDebounceRef.current) {
          clearTimeout(searchDebounceRef.current);
        }
        setStoreSearchTerm("");
        setStoreError("");
        setStoreSuccess("");
        setStoreHasSearched(false);
        setStoreLoading(false);
        setStores(selectedStores.length > 0 ? selectedStores : []);
        setStoreSuggestions([]);
        setSelectedRomMap(
          buildSelectionMap(selectedStores.length > 0 ? selectedStores : []),
        );
        setNotFoundCodes([]);
        return;
      }

      setStoreSearchTerm(value);
      setStoreError("");
      setStoreSuccess("");
      setNotFoundCodes([]);

      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      const bulkCodes = extractBulkCodes(value);
      const hasLineBreak = value.includes("\n") || value.includes("\r");
      const numericTokens = bulkCodes.filter((code) =>
        /^[0-9]+$/.test(code.replace(/^0+/, "")),
      );

      if (hasLineBreak || numericTokens.length >= 2) {
        setStoreSearchTerm("");
        if (bulkCodes.length > 0) {
          handleBulkCodes(bulkCodes);
        }
        return;
      }

      if (value.trim().length < 2) {
        latestStoreSearchRef.current += 1;
        setStoreHasSearched(false);
        setStoreLoading(false);
        setStores(selectedStores.length > 0 ? selectedStores : []);
        setStoreSuggestions([]);
        setSelectedRomMap(
          buildSelectionMap(selectedStores.length > 0 ? selectedStores : []),
        );
        return;
      }

      searchDebounceRef.current = setTimeout(() => {
        performStoreSearch(value);
      }, 350);
    },
    [
      performStoreSearch,
      selectedStores,
      buildSelectionMap,
      handleBulkCodes,
      extractBulkCodes,
    ],
  );

  const handleStoreSelect = useCallback(
    (_event: unknown, value: StoreResult[]) => {
      setStoreError("");
      setStoreSuccess("");

      setSelectedStores(value);

      if (value.length > 0) {
        setStoreHasSearched(true);
        setStores(value);
        setSelectedRomMap(buildSelectionMap(value));
      } else {
        setStores(storeSuggestions);
        setSelectedRomMap(buildSelectionMap(storeSuggestions));
      }
    },
    [storeSuggestions, buildSelectionMap],
  );

  const handleUpdateStoreRom = async (storeId: number) => {
    const selectedRom = selectedRomMap[storeId];

    if (!selectedRom || typeof selectedRom !== "number") {
      setStoreError("Please select a ROM before updating.");
      return;
    }

    const targetStore = stores.find((store) => store.id === storeId);
    if (targetStore && targetStore.rom_id === selectedRom) {
      setStoreError("Store is already assigned to the selected ROM.");
      return;
    }

    setUpdatingStores((prev) => ({ ...prev, [storeId]: true }));
    setStoreError("");
    setStoreSuccess("");

    try {
      const API_URL = API_BASE_URL;
      const response = await fetch(`${API_URL}/spar-mobile/stores/change-rom`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ storeId, romId: selectedRom }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const updatedStore: StoreResult | null = data.data ?? null;

      if (updatedStore) {
        setStores((prev) =>
          prev.map((store) => (store.id === storeId ? updatedStore : store)),
        );
        setStoreSuggestions((prev) =>
          prev.map((store) => (store.id === storeId ? updatedStore : store)),
        );
        setSelectedStores((prev) =>
          prev.map((store) => (store.id === storeId ? updatedStore : store)),
        );
        setSelectedRomMap((prev) => ({
          ...prev,
          [storeId]: updatedStore.rom_id ?? "",
        }));
        setStoreSuccess(
          `Store "${updatedStore.name}" reassigned to ${updatedStore.rom_name || "selected ROM"}.`,
        );
      } else {
        await performStoreSearch(storeSearchTerm);
        setStoreSuccess("Store ROM updated.");
      }

      setTimeout(() => setStoreSuccess(""), 6000);
    } catch (err: any) {
      console.error("Failed to update store ROM", err);
      setStoreError(err.message || "Failed to update store ROM");
    } finally {
      setUpdatingStores((prev) => {
        const next = { ...prev };
        delete next[storeId];
        return next;
      });
    }
  };

  const loadVoucherMatchingSummary = useCallback(async () => {
    if (!matchingMonth) {
      return;
    }

    setMatchingLoading(true);
    setMatchingError("");
    setMatchingSuccess("");

    try {
      const { start, end } = getMonthDateRange(matchingMonth);
      const params = new URLSearchParams();
      params.set("startDate", start);
      params.set("endDate", end);

      const API_URL = API_BASE_URL;
      const queryString = params.toString();
      const response = await fetch(
        `${API_URL}/spar-mobile/voucher-matching/summary${queryString ? `?${queryString}` : ""}`,
        {
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to load voucher matching summary (HTTP ${response.status})`,
        );
      }

      const result = await response.json();
      setMatchingSummary(result.data || null);
      lastLoadedMatchingMonthRef.current = matchingMonth;
    } catch (err: any) {
      console.error("Failed to load voucher matching summary", err);
      setMatchingSummary(null);
      setMatchingError(
        err.message || "Failed to load voucher matching summary",
      );
    } finally {
      setMatchingLoading(false);
    }
  }, [matchingMonth]);

  const loadUnmatchedVouchers = useCallback(async () => {
    if (!matchingMonth) {
      return;
    }

    setUnmatchedLoading(true);
    setUnmatchedError("");
    setMatchingSuccess("");

    try {
      const { start, end } = getMonthDateRange(matchingMonth);
      const params = new URLSearchParams();
      params.set("startDate", start);
      params.set("endDate", end);
      params.set("limit", String(unmatchedLimit));

      if (unmatchedStateFilter && unmatchedStateFilter !== "ANY") {
        params.set("state", unmatchedStateFilter);
      }

      if (unmatchedMatchedFilter && unmatchedMatchedFilter !== "ANY") {
        params.set(
          "matchedStatus",
          unmatchedMatchedFilter === "NULL" ? "null" : unmatchedMatchedFilter,
        );
      }

      if (unmatchedMarkupFilter && unmatchedMarkupFilter !== "ANY") {
        params.set(
          "markupStatus",
          unmatchedMarkupFilter === "NULL" ? "null" : unmatchedMarkupFilter,
        );
      }

      if (!onlyProblematic) {
        params.set("onlyProblematic", "false");
      }

      const API_URL = API_BASE_URL;
      const queryString = params.toString();
      const response = await fetch(
        `${API_URL}/spar-mobile/voucher-matching/unmatched${queryString ? `?${queryString}` : ""}`,
        {
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to load unmatched vouchers (HTTP ${response.status})`,
        );
      }

      const result = await response.json();
      const payload = result.data || {};
      setUnmatchedVouchers(payload.vouchers || []);
      setUnmatchedMeta({
        count:
          payload.count ?? (payload.vouchers ? payload.vouchers.length : 0),
        limit: payload.limit ?? unmatchedLimit,
        range: payload.range
          ? { start: payload.range.start, end: payload.range.end }
          : undefined,
      });
      lastLoadedMatchingMonthRef.current = matchingMonth;
    } catch (err: any) {
      console.error("Failed to load unmatched vouchers", err);
      setUnmatchedVouchers([]);
      setUnmatchedMeta(null);
      setUnmatchedError(err.message || "Failed to load unmatched vouchers");
    } finally {
      setUnmatchedLoading(false);
    }
  }, [
    matchingMonth,
    unmatchedLimit,
    unmatchedStateFilter,
    unmatchedMatchedFilter,
    unmatchedMarkupFilter,
    onlyProblematic,
  ]);

  const loadVoucherRunStatuses = useCallback(async () => {
    if (!matchingMonth) return;

    const requestId = ++processStatusRequestRef.current;
    setProcessStatusAvailability("loading");
    setProcessStatusError("");
    setProcessStatuses({});
    try {
      const { start, end } = getMonthDateRange(matchingMonth);
      const params = new URLSearchParams({ startDate: start, endDate: end });
      const API_URL = API_BASE_URL;
      const response = await fetch(
        `${API_URL}/spar-mobile/voucher-matching/runs?${params.toString()}`,
        { credentials: "include" },
      );
      if (!response.ok) {
        throw new Error(
          `Failed to load reconciliation status (HTTP ${response.status})`,
        );
      }
      const result = await response.json();
      const indexed = indexRunStatuses(
        result.data as VoucherMatchingRunsResult,
      );
      if (requestId !== processStatusRequestRef.current) return;
      setProcessStatuses(indexed);
      setProcessStatusAvailability("available");
    } catch (err: any) {
      if (requestId !== processStatusRequestRef.current) return;
      console.error("Failed to load voucher reconciliation status", err);
      setProcessStatuses({});
      setProcessStatusAvailability("unavailable");
      setProcessStatusError(
        err.message || "Failed to load voucher reconciliation status",
      );
    }
  }, [matchingMonth]);

  const refreshVoucherMatchingData = useCallback(async () => {
    await Promise.all([
      loadVoucherMatchingSummary(),
      loadUnmatchedVouchers(),
      loadVoucherRunStatuses(),
    ]);
  }, [loadUnmatchedVouchers, loadVoucherMatchingSummary, loadVoucherRunStatuses]);

  const executeProcess = useCallback(
    async (type: VoucherMatchingProcess, date: string) => {
      setRunningProcess({ type, date });
      setMatchingError("");
      setMatchingSuccess("");

      try {
        const API_URL = API_BASE_URL;
        // Format date as YYYY-MM-DD (extract just the date part if it's a full ISO string or datetime)
        let dateStr = date;
        if (date.includes("T")) {
          dateStr = date.split("T")[0];
        } else if (date.includes(" ")) {
          dateStr = date.split(" ")[0];
        }
        // Ensure it's in YYYY-MM-DD format
        if (dateStr && !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          // Try to parse and reformat
          const parsed = new Date(date);
          if (!isNaN(parsed.getTime())) {
            const year = parsed.getFullYear();
            const month = String(parsed.getMonth() + 1).padStart(2, "0");
            const day = String(parsed.getDate()).padStart(2, "0");
            dateStr = `${year}-${month}-${day}`;
          }
        }
        const params: Record<string, string> = {
          start: dateStr,
          end: dateStr,
        };
        const endpoint =
          type === "markup"
            ? "/spar-electrum-recon/markup/download"
            : "/spar-electrum-recon/matched/download";

        const queryString = new URLSearchParams(params).toString();
        const url = `${API_URL}${endpoint}${queryString ? `?${queryString}` : ""}`;

        const response = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || `Failed to run ${type} process`);
        }

        const result = await response.json();

        const missingFiles =
          result.missingMatchedFiles || result.missingMarkupFiles;
        await loadVoucherRunStatuses();
        if (result.success) {
          setMatchingSuccess(
            `${type.charAt(0).toUpperCase() + type.slice(1)} process completed successfully for ${date}`,
          );
          await loadVoucherMatchingSummary();
        } else if (missingFiles && missingFiles.length > 0) {
          setMatchingError(
            `${type.charAt(0).toUpperCase() + type.slice(1)} file(s) not found on SFTP for date(s): ${missingFiles.join(", ")}. The file may not have been generated by Electrum yet.`,
          );
        } else {
          setMatchingError(result.message || "Process failed");
        }
      } catch (err: any) {
        setMatchingError(err.message || `Failed to run ${type} process`);
        console.error(`Error running ${type} process:`, err);
      } finally {
        setRunningProcess(null);
      }
    },
    [loadVoucherMatchingSummary, loadVoucherRunStatuses],
  );

  // Run a process for a specific date
  const handleRunProcess = useCallback(
    async (type: VoucherMatchingProcess, date: string) => {
      const actionState = getVoucherRunActionState({
        availability: processStatusAvailability,
        run: processStatuses[date]?.[type],
      });
      if (actionState.disabled) return;

      if (actionState.confirmRerun) {
        setConfirmDialog({ open: true, type, date });
      } else {
        await executeProcess(type, date);
      }
    },
    [executeProcess, processStatusAvailability, processStatuses],
  );

  const handleConfirmRun = useCallback(() => {
    const { type, date } = confirmDialog;
    setConfirmDialog({ open: false, type: "", date: "" });
    if (type) executeProcess(type, date);
  }, [confirmDialog, executeProcess]);

  const fixReversedTransactions = useCallback(async () => {
    if (!matchingMonth) {
      setMatchingError("Please select a month before running the quick fix.");
      return;
    }

    const { start, end } = getMonthDateRange(matchingMonth);
    const monthLabel = new Date(`${matchingMonth}-01`).toLocaleDateString(
      "en-US",
      { year: "numeric", month: "long" },
    );
    const confirmMessage = reversedQuickFixConfirmation(
      monthLabel,
      start,
      end,
    );
    if (!confirm(confirmMessage)) {
      return;
    }

    setFixingReversed(true);
    setMatchingError("");
    setMatchingSuccess("");

    try {
      const API_URL = API_BASE_URL;
      const response = await fetch(
        `${API_URL}/spar-mobile/voucher-matching/fix-reversed`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            startDate: start,
            endDate: end,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fix reversed vouchers (HTTP ${response.status})`,
        );
      }

      const result = await response.json();
      setMatchingSuccess(
        reversedQuickFixSuccess({
          reversedCount: Number(result?.data?.reversedCount ?? 0),
          matchedOnlyCount: Number(result?.data?.matchedOnlyCount ?? 0),
        }),
      );
      await refreshVoucherMatchingData();
    } catch (err: any) {
      console.error("Failed to fix reversed vouchers", err);
      setMatchingError(err.message || "Failed to fix reversed vouchers");
    } finally {
      setFixingReversed(false);
    }
  }, [matchingMonth, refreshVoucherMatchingData]);

  const fixAuthorisedTransactions = useCallback(async () => {
    if (!matchingMonth) {
      setMatchingError("Please select a month before running the quick fix.");
      return;
    }

    const { start, end } = getMonthDateRange(matchingMonth);
    const monthLabel = new Date(`${matchingMonth}-01`).toLocaleDateString(
      "en-US",
      { year: "numeric", month: "long" },
    );
    const confirmMessage = `Mark authorised vouchers as matched for ${monthLabel} (${start} → ${end})?`;
    if (!confirm(confirmMessage)) {
      return;
    }

    setFixingAuthorised(true);
    setMatchingError("");
    setMatchingSuccess("");

    try {
      const API_URL = API_BASE_URL;
      const response = await fetch(
        `${API_URL}/spar-mobile/voucher-matching/fix-authorised`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            startDate: start,
            endDate: end,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fix authorised vouchers (HTTP ${response.status})`,
        );
      }

      const result = await response.json();
      const updated = Number(result?.data?.updated ?? 0);
      const matchedFixCount = Number(result?.data?.matchedFixCount ?? 0);
      const unmatchedFixCount = Number(result?.data?.unmatchedFixCount ?? 0);
      if (updated === 0) {
        setMatchingSuccess("No authorised vouchers required updates.");
      } else {
        const parts: string[] = [];
        if (matchedFixCount > 0) {
          parts.push(
            `${matchedFixCount} matched-file fix${matchedFixCount === 1 ? "" : "es"}`,
          );
        }
        if (unmatchedFixCount > 0) {
          parts.push(
            `${unmatchedFixCount} markup-unmatched fix${unmatchedFixCount === 1 ? "" : "es"}`,
          );
        }
        const detail = parts.length > 0 ? ` (${parts.join(", ")})` : "";
        setMatchingSuccess(
          `Marked ${updated} authorised voucher${updated === 1 ? "" : "s"} as matched${detail}.`,
        );
      }
      await refreshVoucherMatchingData();
    } catch (err: any) {
      console.error("Failed to fix authorised vouchers", err);
      setMatchingError(err.message || "Failed to fix authorised vouchers");
    } finally {
      setFixingAuthorised(false);
    }
  }, [matchingMonth, refreshVoucherMatchingData]);

  const unmatchedStateOptions = useMemo(
    () => ["ANY", "AUTHORISED", "CONFIRMED", "REVERSED"],
    [],
  );
  const unmatchedMatchedOptions = useMemo(
    () => ["ANY", "MATCHED", "UNMATCHED", "NULL"],
    [],
  );
  const unmatchedMarkupOptions = useMemo(
    () => ["ANY", "MATCHED", "UNMATCHED", "NULL"],
    [],
  );
  const unmatchedLimitOptions = useMemo(() => [25, 50, 100, 200, 500], []);

  const voucherSummaryCards = useMemo(() => {
    if (!matchingSummary) return [];

    const total = matchingSummary.totals.total || 0;
    const matchedPct =
      total > 0
        ? Math.round((matchingSummary.totals.matched / total) * 100)
        : 0;
    const markupMatchedPct =
      total > 0
        ? Math.round((matchingSummary.totals.markupMatched / total) * 100)
        : 0;

    return [
      {
        key: "total",
        label: "Total vouchers",
        value: matchingSummary.totals.total,
      },
      {
        key: "markupUnmatched",
        label: "Markup not matched",
        value: matchingSummary.totals.markupUnmatched,
        color: "warning.main",
        helper: `${markupMatchedPct}% markup matched`,
      },
      {
        key: "matched",
        label: "Matched vouchers",
        value: matchingSummary.totals.matched,
        color: "success.main",
        helper: `${matchedPct}% matched`,
      },
      {
        key: "unmatched",
        label: "Unmatched vouchers",
        value: matchingSummary.totals.unmatched,
        color: "error.main",
      },
      {
        key: "confirmedIssues",
        label: "Confirmed needing action",
        value: matchingSummary.totals.confirmedUnmatched,
        color: "error.dark",
      },
      {
        key: "authorisedPending",
        label: "Authorised pending",
        value: matchingSummary.totals.authorisedPending,
        color: "info.main",
      },
    ];
  }, [matchingSummary]);

  const processReward = async (i_customer: number) => {
    setProcessing(i_customer);
    setError("");
    setSuccess("");

    try {
      const API_URL = API_BASE_URL;
      const response = await fetch(`${API_URL}/spar-mobile/process-reward`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ i_customer }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      setSuccess(
        `Published to Pub/Sub - Topic: ${data.pubsub?.topic || "reward-event-topic"}, Message ID: ${data.messageId}`,
      );
      setTimeout(() => setSuccess(""), 8000);
    } catch (err: any) {
      setError(err.message || "Failed to process reward");
    } finally {
      setProcessing(null);
    }
  };

  const exportPendingCustomerIds = async () => {
    if (statusFilter !== "pending") {
      setError(
        'Export is only available when status filter is set to "pending"',
      );
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const API_URL = API_BASE_URL;
      const baseEndpoint = isPromoDataRoute
        ? `${API_URL}/promo-data/rewards-engine/export-customer-ids`
        : `${API_URL}/spar-mobile/pending-rewards/export-customer-ids`;

      const params = new URLSearchParams();
      params.append("status", "pending");

      const endpoint = `${baseEndpoint}?${params.toString()}`;

      console.log("[EXPORT] Fetching all customer IDs from:", endpoint);
      const response = await fetch(endpoint, {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[EXPORT] Error response:", response.status, errorText);
        throw new Error(
          `Failed to fetch: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      console.log("[EXPORT] Received", data.count, "customer records");

      if (!data.customerData || data.customerData.length === 0) {
        setError("No pending customer data to export");
        setLoading(false);
        return;
      }

      // Create CSV content with headers and data
      const headers = ["i_customer", "spend_30_day", "reward_value"];
      const rows = data.customerData.map(
        (customer: {
          i_customer: number;
          spend_30_day: number;
          value: number;
        }) => [
          customer.i_customer.toString(),
          customer.spend_30_day.toFixed(2),
          customer.value.toFixed(2),
        ],
      );

      // Escape CSV values (handle commas, quotes, newlines)
      const escapeCsvValue = (value: string) => {
        if (
          value.includes(",") ||
          value.includes('"') ||
          value.includes("\n")
        ) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      };

      const csvContent = [
        headers.join(","),
        ...rows.map((row: string[]) => row.map(escapeCsvValue).join(",")),
      ].join("\n");

      // Create and download the file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      const dateStr = new Date().toISOString().split("T")[0];
      link.setAttribute("href", url);
      link.setAttribute("download", `pending-rewards-export-${dateStr}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccess(
        `Exported ${data.count} customer records (i_customer, spend, reward value) successfully`,
      );
      setTimeout(() => setSuccess(""), 5000);
    } catch (err: any) {
      console.error("[EXPORT] Error:", err);
      setError(err.message || "Failed to export customer IDs");
    } finally {
      setLoading(false);
    }
  };

  const processAllRewards = async () => {
    if (!confirm(`Process all ${pendingRewards.length} rewards on this page?`))
      return;

    setProcessingAll(true);
    setError("");
    setSuccess("");

    const total = pendingRewards.length;
    const i_customers = pendingRewards.map((r) => r.i_customer);

    try {
      const API_URL = API_BASE_URL;
      setProcessProgress({ current: 0, total });

      const response = await fetch(
        `${API_URL}/spar-mobile/process-rewards-batch`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ i_customers }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setProcessProgress({ current: total, total });
      setSuccess(
        `Batch Complete - Total: ${data.total}, Succeeded: ${data.succeeded}, Failed: ${data.failed}`,
      );
    } catch (err: any) {
      console.error("[PENDING REWARDS] Batch process error:", err);
      setError(err.message || "Failed to process batch");
    } finally {
      setProcessingAll(false);
      setTimeout(() => setProcessProgress({ current: 0, total: 0 }), 2000);
    }
  };

  const updateCustomerSpend = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const API_URL = API_BASE_URL;
      const endpoint = isPromoDataRoute
        ? `${API_URL}/promo-data/update-customer-spend`
        : `${API_URL}/spar-mobile/update-customer-spend`;

      setSuccess("Updating customer spend calculations...");

      const response = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setSuccess(
        `Customer spend updated successfully (took ${(data.durationMs / 1000).toFixed(1)}s). Refreshing data...`,
      );

      // Reload the rewards data after updating spend
      setTimeout(() => {
        loadPendingRewards();
      }, 1000);
    } catch (err: any) {
      console.error("[PENDING REWARDS] Update customer spend error:", err);
      setError(err.message || "Failed to update customer spend");
    } finally {
      setLoading(false);
    }
  };

  const processAllCustomers = async () => {
    const totalCustomers = pagination?.total || 0;
    if (totalCustomers === 0) {
      setError("No customers found to process");
      return;
    }

    if (
      !confirm(
        `Process ALL ${totalCustomers.toLocaleString()} customers with pending rewards?\n\nThis will process all customers in the database, not just the ones on this page. This may take several minutes.`,
      )
    )
      return;

    setProcessingAll(true);
    setError("");
    setSuccess("");

    try {
      const API_URL = API_BASE_URL;
      const endpoint = isPromoDataRoute
        ? `${API_URL}/promo-data/process-all-pending-rewards`
        : `${API_URL}/spar-mobile/process-all-pending-rewards`;

      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      params.append("batchSize", "1000");

      setProcessProgress({ current: 0, total: totalCustomers });

      const response = await fetch(`${endpoint}?${params.toString()}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setProcessProgress({ current: totalCustomers, total: totalCustomers });
      setSuccess(
        `All Customers Processed - Total: ${data.total?.toLocaleString() || 0}, ` +
          `Succeeded: ${data.succeeded?.toLocaleString() || 0}, ` +
          `Failed: ${data.failed?.toLocaleString() || 0}, ` +
          `Batches: ${data.batchesProcessed || 0}`,
      );
    } catch (err: any) {
      console.error("[PENDING REWARDS] Process all customers error:", err);
      setError(err.message || "Failed to process all customers");
    } finally {
      setProcessingAll(false);
      setTimeout(() => setProcessProgress({ current: 0, total: 0 }), 5000);
    }
  };

  const processFailedReward = async (id: number) => {
    setProcessingFailed(id);
    setErrorFailed("");
    setSuccessFailed("");

    try {
      const API_URL = API_BASE_URL;
      const response = await fetch(
        `${API_URL}/spar-mobile/process-failed-reward`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id }),
        },
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      setSuccessFailed(`Record ${id} marked as processed`);
      setFailedRewards((prev) => prev.filter((r) => r.id !== id));
      setTimeout(() => setSuccessFailed(""), 5000);
    } catch (err: any) {
      setErrorFailed(err.message || "Failed to process record");
    } finally {
      setProcessingFailed(null);
    }
  };

  const openEmailModal = (id: number, currentEmail: string) => {
    setEditingEmail({ id, currentEmail });
    setNewEmail(currentEmail || "");
  };

  const closeEmailModal = () => {
    setEditingEmail(null);
    setNewEmail("");
  };

  const saveEmailAndProcess = async () => {
    if (!editingEmail) return;

    setProcessingFailed(editingEmail.id);
    setErrorFailed("");
    setSuccessFailed("");

    try {
      const API_URL = API_BASE_URL;
      const response = await fetch(
        `${API_URL}/spar-mobile/update-email-and-process`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: editingEmail.id, email: newEmail }),
        },
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      setSuccessFailed(
        `Email updated to "${newEmail}" and record marked as processed`,
      );
      setFailedRewards((prev) => prev.filter((r) => r.id !== editingEmail.id));
      closeEmailModal();
      setTimeout(() => setSuccessFailed(""), 5000);
    } catch (err: any) {
      setErrorFailed(err.message || "Failed to update email");
    } finally {
      setProcessingFailed(null);
    }
  };

  const processAllFailedRewards = async () => {
    const itemsToProcess = filteredFailedRewards;
    const filterText =
      errorTypeFilter === "all"
        ? "all failed registrations"
        : `${errorTypeFilter} errors`;

    if (!confirm(`Process ${itemsToProcess.length} ${filterText}?`)) return;

    setProcessingAllFailed(true);
    setErrorFailed("");
    setSuccessFailed("");

    const total = itemsToProcess.length;
    let succeeded = 0;
    let failed = 0;

    for (let i = 0; i < itemsToProcess.length; i++) {
      setProcessProgressFailed({ current: i + 1, total });

      try {
        const API_URL = API_BASE_URL;
        const response = await fetch(
          `${API_URL}/spar-mobile/process-failed-reward`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: itemsToProcess[i].id }),
          },
        );

        if (response.ok) succeeded++;
        else failed++;
      } catch {
        failed++;
      }

      // Rate limit: wait 1 second between requests to avoid overwhelming the API
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    setSuccessFailed(
      `Batch Complete - Total: ${total}, Succeeded: ${succeeded}, Failed: ${failed}`,
    );
    // Remove processed items from the list
    const processedIds = itemsToProcess.map((item) => item.id);
    setFailedRewards((prev) =>
      prev.filter((r) => !processedIds.includes(r.id)),
    );
    setTimeout(() => loadFailedRewards(), 2000);
    setProcessingAllFailed(false);
    setProcessProgressFailed({ current: 0, total: 0 });
  };

  const loadReconciliation = useCallback(async (date: string) => {
    setReconciliationLoading(true);
    setReconciliationError("");
    try {
      console.log("[FRONTEND] Loading reconciliation for date:", date);
      const res = await api.get(
        `/spar-mobile/incentive-reconciliation/${date}`,
      );
      console.log("[FRONTEND] Reconciliation API response:", res.data);
      console.log("[FRONTEND] Reconciliation data:", res.data?.data);
      setReconciliationData(res.data?.data || null);
    } catch (err: any) {
      console.error("Failed to load reconciliation:", err);
      setReconciliationError(
        err.response?.data?.message || "Failed to load reconciliation data",
      );
      setReconciliationData(null);
    } finally {
      setReconciliationLoading(false);
    }
  }, []);

  const loadMonthlySummary = useCallback(async (yearMonth: string) => {
    setMonthlyLoading(true);
    setMonthlyError("");
    try {
      const [year, month] = yearMonth.split("-").map(Number);
      console.log("[FRONTEND] Loading monthly summary for:", year, month);
      const res = await api.get(
        `/spar-mobile/incentive-reconciliation-monthly/${year}/${month}`,
      );
      console.log("[FRONTEND] Monthly summary API response:", res.data);
      console.log("[FRONTEND] Monthly summary data:", res.data?.data);
      setMonthlySummary(res.data?.data || null);
      lastLoadedMonthRef.current = yearMonth; // Track the last loaded month
    } catch (err: any) {
      console.error("Failed to load monthly summary:", err);
      setMonthlyError(
        err.response?.data?.message || "Failed to load monthly summary",
      );
      setMonthlySummary(null);
      lastLoadedMonthRef.current = null;
    } finally {
      setMonthlyLoading(false);
    }
  }, []);

  const calculateReconciliation = useCallback(async (silent = false) => {
    setCalculating(true);
    if (!silent) {
      setCalculationError("");
      setCalculationSuccess("");
    }
    try {
      console.log("[FRONTEND] Triggering reconciliation calculation");
      const res = await api.post(
        "/spar-mobile/incentive-reconciliation/calculate",
      );
      console.log("[FRONTEND] Calculation response:", res.data);
      if (!silent) {
        setCalculationSuccess(
          res.data?.data?.message ||
            "Reconciliation calculation completed successfully",
        );
      }
      // Reset the lastLoadedMonthRef so the useEffect will reload the data
      lastLoadedMonthRef.current = null;
      setInitialCalculationDone(true);
      console.log(
        "[FRONTEND] Calculation complete, cleared lastLoadedMonthRef to trigger reload",
      );
    } catch (err: any) {
      console.error("Failed to calculate reconciliation:", err);
      if (!silent) {
        setCalculationError(
          err.response?.data?.message || "Failed to calculate reconciliation",
        );
      }
    } finally {
      setCalculating(false);
    }
  }, []);

  const allocateIncentiveData = useCallback(
    async (date: string) => {
      setAllocating(true);
      setAllocationError("");
      setAllocationSuccess("");
      try {
        console.log("[FRONTEND] Allocating incentive data for date:", date);
        const res = await api.post(
          "/spar-mobile/incentive-allocation/allocate",
          { date },
        );
        console.log("[FRONTEND] Allocation response:", res.data);
        setAllocationSuccess(
          res.data?.data?.message ||
            `Incentive data allocated successfully for ${date}. ${res.data?.data?.data?.length || 0} distributors processed.`,
        );

        // After successful allocation, trigger reconciliation calculation (silently)
        console.log(
          "[FRONTEND] Triggering reconciliation calculation after allocation",
        );
        try {
          await calculateReconciliation(true); // Silent mode - don't show success/error messages
        } catch (calcErr: any) {
          console.error(
            "Failed to calculate reconciliation after allocation:",
            calcErr,
          );
          // Don't fail the allocation if calculation fails, just log it
          setAllocationSuccess(
            (prev) =>
              prev + " (Note: Reconciliation calculation encountered an issue)",
          );
        }

        // Reload daily reconciliation so UI shows new allocation and disables Allocate button
        if (reconciliationDate === date) {
          await loadReconciliation(date);
        }
        // Refresh monthly summary if loaded so the calendar row shows allocated when they go back
        if (selectedMonth) {
          loadMonthlySummary(selectedMonth);
        }
      } catch (err: any) {
        console.error("Failed to allocate incentive data:", err);
        setAllocationError(
          err.response?.data?.message || "Failed to allocate incentive data",
        );
      } finally {
        setAllocating(false);
      }
    },
    [
      reconciliationDate,
      loadReconciliation,
      calculateReconciliation,
      selectedMonth,
      loadMonthlySummary,
    ],
  );

  useEffect(() => {
    if (currentSection === "promo-engine") {
      loadPendingRewards();
    } else if (currentSection === "spar-rewards") {
      loadFailedRewards();
    } else if (currentSection === "rom-management") {
      ensureRomOptions();
    }
    // Note: incentive-reconciliation loading is handled by the separate useEffect below
  }, [
    currentSection,
    ensureRomOptions,
    loadFailedRewards,
    loadPendingRewards,
  ]);

  // Reset page when status filter changes
  useEffect(() => {
    if (currentSection === "promo-engine" && statusFilter) {
      setPage(0);
    }
  }, [statusFilter, currentSection]);

  // Reload pending rewards when status filter or pagination changes (but not when loadPendingRewards changes)
  useEffect(() => {
    if (currentSection === "promo-engine") {
      loadPendingRewards(page, rowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, currentSection, page, rowsPerPage]);

  // Auto-load voucher matching data when month changes
  useEffect(() => {
    if (
      currentSection === "voucher-matching" &&
      matchingMonth &&
      !matchingLoading &&
      !unmatchedLoading &&
      lastLoadedMatchingMonthRef.current !== matchingMonth // Only load if it's a different month
    ) {
      lastLoadedMatchingMonthRef.current = matchingMonth; // Set before loading to prevent duplicate calls
      void loadVoucherMatchingSummary();
      void loadUnmatchedVouchers();
      void loadVoucherRunStatuses();
    }
  }, [
    matchingMonth,
    currentSection,
    matchingLoading,
    unmatchedLoading,
    loadVoucherMatchingSummary,
    loadUnmatchedVouchers,
    loadVoucherRunStatuses,
  ]);

  // Auto-calculate reconciliation when section opens
  useEffect(() => {
    if (
      currentSection === "incentive-reconciliation" &&
      !calculating &&
      reconciliationCalculatedRef.current !== currentSection // Only calculate once per section load
    ) {
      reconciliationCalculatedRef.current = currentSection; // Mark as calculated
      console.log("[FRONTEND] Auto-calculating reconciliation on section load");
      void calculateReconciliation(true); // Silent mode - automatic calculation
    } else if (currentSection !== "incentive-reconciliation") {
      // Reset ref and state when leaving the section
      reconciliationCalculatedRef.current = null;
      setInitialCalculationDone(false);
    }
  }, [currentSection, calculating, calculateReconciliation]);

  // Auto-load/reload monthly summary when month changes or section opens (only in monthly view mode)
  // Don't load while calculation is in progress - wait for calculation to complete and trigger the reload
  // Also don't load on initial section load - wait for the calculation to complete first
  useEffect(() => {
    if (
      currentSection === "incentive-reconciliation" &&
      viewMode === "monthly" &&
      selectedMonth &&
      !monthlyLoading &&
      !calculating && // Don't load while calculating - calculation will trigger the reload
      initialCalculationDone && // Only load after initial calculation has completed
      lastLoadedMonthRef.current !== selectedMonth // Only load if it's a different month
    ) {
      console.log(
        "[FRONTEND] Auto-loading monthly summary for:",
        selectedMonth,
      );
      void loadMonthlySummary(selectedMonth);
    }
  }, [
    selectedMonth,
    currentSection,
    viewMode,
    monthlyLoading,
    calculating,
    initialCalculationDone,
    loadMonthlySummary,
  ]);

  // One-time calculate when entering expired-rewards section (mirrors incentive-reconciliation)
  useEffect(() => {
    if (
      currentSection === "expired-rewards" &&
      !calculatingExpiredHistory &&
      expiredRewardsCalculatedRef.current !== currentSection
    ) {
      expiredRewardsCalculatedRef.current = currentSection;
      console.log(
        "[FRONTEND] Auto-calculating expired rewards history on section load (fixed range)",
      );
      void calculateExpiredRewardsHistory(true); // Backend uses fixed start to yesterday (same as incentive)
    } else if (currentSection !== "expired-rewards") {
      expiredRewardsCalculatedRef.current = null;
      setExpiredRewardsInitialCalculationDone(false);
    }
  }, [
    currentSection,
    calculatingExpiredHistory,
    calculateExpiredRewardsHistory,
  ]);

  // Load monthly immediately when on section (don't wait for calculate). Refresh when calculate completes.
  useEffect(() => {
    if (
      currentSection === "expired-rewards" &&
      expiredRewardsSelectedMonth &&
      !expiredRewardsMonthlyLoading &&
      lastLoadedExpiredMonthRef.current !== expiredRewardsSelectedMonth
    ) {
      lastLoadedExpiredMonthRef.current = expiredRewardsSelectedMonth;
      void loadExpiredRewardsMonthly(expiredRewardsSelectedMonth);
    }
  }, [
    currentSection,
    expiredRewardsSelectedMonth,
    expiredRewardsMonthlyLoading,
    loadExpiredRewardsMonthly,
    expiredRewardsInitialCalculationDone, // when calc completes we clear ref and reload (see below)
  ]);

  return (
    <div>
      <PageHeader
        title={
          isPromoDataRoute
            ? "Rewards Engine"
            : currentSection === "spar-rewards"
              ? "Failed SPAR Registrations"
              : currentSection === "rom-management"
                ? "ROM Management"
                : currentSection === "store-ownership-change"
                  ? "Store Ownership Change"
                  : currentSection === "voucher-matching"
                    ? "Voucher Matching Health"
                    : currentSection === "expired-rewards"
                      ? "Expired Rewards Write-back"
                      : currentSection === "incentive-reconciliation"
                        ? "Incentive Reconciliation"
                        : currentSection === "retailer-commissions"
                          ? "Retailer Commissions"
                          : "SPAR Mobile"
        }
        subtitle={
          isPromoDataRoute
            ? "Manage promotional rewards for all distributors"
            : currentSection === "spar-rewards"
              ? "Process and retry failed SPAR Mobile registrations"
              : currentSection === "rom-management"
                ? "Assign Regional Operations Managers to SPAR Mobile stores"
                : currentSection === "store-ownership-change"
                  ? "Create a new sub-distributor for a store and migrate all customers, SIM stock and portal user access from the old store"
                  : currentSection === "voucher-matching"
                    ? "Manage the retailer store master, monitor Nova voucher matching status, and fix common reconciliation issues."
                    : currentSection === "expired-rewards"
                      ? "View expired rewards and write them back to the promo data balance"
                      : currentSection === "incentive-reconciliation"
                        ? "Reconcile SIM card scans with allocated promo data to ensure accurate incentive distribution"
                        : currentSection === "retailer-commissions"
                          ? "Upload monthly commission CSV files. Each month's data can be replaced by uploading a new file for the same month."
                          : "Manage SPAR Mobile rewards and registrations"
        }
      />

      {/* Tab 1: Promo Engine */}
      {currentSection === "promo-engine" && (
        <div className="p-6">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Pending Rewards</h3>
            </div>
            <div className="flex flex-row flex-wrap gap-4">
              <Select
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value ?? "all")}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="allocated">Allocated</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={() => loadPendingRewards()}
                disabled={loading || processingAll}
                size="sm"
              >
                <RefreshIcon className="mr-2 h-4 w-4" />
                {loading ? "Refreshing..." : "Refresh"}
              </Button>
              <Button
                variant="outline"
                onClick={updateCustomerSpend}
                disabled={loading || processingAll}
                title="Update customer spend calculations (calls stored procedure)"
                size="sm"
              >
                <RefreshIcon className="mr-2 h-4 w-4" />
                {loading ? "Updating..." : "Update Spend"}
              </Button>
            </div>
            <div className="flex flex-row gap-4 mt-4">
              <Button
                variant="secondary"
                onClick={processAllRewards}
                disabled={
                  loading || processingAll || pendingRewards.length === 0
                }
              >
                <ProcessIcon className="mr-2 h-4 w-4" />
                {processingAll
                  ? `Processing ${processProgress.current}/${processProgress.total}...`
                  : `Process Page (${pendingRewards.length})`}
              </Button>
              <Button
                onClick={processAllCustomers}
                disabled={loading || processingAll || !pagination?.total}
                title={`Process all ${pagination?.total?.toLocaleString() || 0} customers with pending rewards`}
              >
                <ProcessIcon className="mr-2 h-4 w-4" />
                {processingAll
                  ? `Processing ${processProgress.current?.toLocaleString() || 0}/${processProgress.total?.toLocaleString() || 0}...`
                  : `Process All (${pagination?.total?.toLocaleString() || 0})`}
              </Button>
              {statusFilter === "pending" && (
                <Button
                  variant="outline"
                  onClick={exportPendingCustomerIds}
                  disabled={
                    loading || !pagination?.total || pagination.total === 0
                  }
                  title="Export all pending rewards: i_customer, spend_30_day, and reward_value (all pages, not just current page)"
                >
                  <DownloadIcon className="mr-2 h-4 w-4" />
                  Export All ({pagination?.total?.toLocaleString() || 0})
                </Button>
              )}
            </div>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert className="mb-4 border-green-200 bg-green-50 text-green-800">
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {pendingRewards.length === 0 && !loading ? (
            <Alert className="border-blue-200 bg-blue-50 text-blue-800">
              <AlertDescription>No pending rewards found</AlertDescription>
            </Alert>
          ) : (
            <>
              <Alert className="mb-4 border-blue-200 bg-blue-50 text-blue-800">
                <AlertDescription>
                  Showing {pendingRewards.length} of {pagination?.total || 0}{" "}
                  {statusFilter !== "all" ? statusFilter : ""} rewards
                  {statusFilter === "all"
                    ? "(status ≠ allocated)"
                    : `(status = ${statusFilter})`}
                  {" - Only customers with spend > R19"}
                  {pagination &&
                    ` - Page ${pagination.page} of ${pagination.pages}`}
                </AlertDescription>
              </Alert>

              <div className="relative">
                {loading && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded">
                    <IconLoader2 className="h-6 w-6 animate-spin" />
                  </div>
                )}
                <div className={cn("overflow-auto transition-opacity duration-200", loading && "opacity-50")}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead className="text-right">Spend & Reward</TableHead>
                        <TableHead>
                          <strong>Status</strong>
                        </TableHead>
                        <TableHead>
                          <strong>Reward Sources</strong>
                        </TableHead>
                        <TableHead>
                          <strong>Spend Requirement & Check Date</strong>
                        </TableHead>
                        <TableHead className="text-center">
                          <strong>Created Date</strong>
                        </TableHead>
                        <TableHead className="text-center">
                          <strong>Expires At</strong>
                        </TableHead>
                        <TableHead className="text-center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingRewards.map((reward, index) => {
                        const rewardSources = reward.reward_sources
                          ? reward.reward_sources
                              .split(", ")
                              .filter((s) => s && s !== "unknown")
                          : [];
                        return (
                          <TableRow key={index} className="hover:bg-muted/50">
                            <TableCell>
                              <div>
                                <p className="text-sm font-medium">
                                  {reward.i_customer}
                                </p>
                                <span className="text-xs text-muted-foreground">
                                  {reward.distributor_name ||
                                    `Dist: ${reward.distributor_i_customer || "N/A"}`}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div>
                                <p className="text-sm">
                                  {formatRands(reward.spend_30_day || 0)}
                                </p>
                                <p className="text-sm font-bold text-blue-600">
                                  {formatMB(reward.value)}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                // Check if status should be "spend not met"
                                let displayStatus = reward.status || "N/A";
                                let chipColor:
                                  | "success"
                                  | "warning"
                                  | "error"
                                  | "default" = "default";

                                if (
                                  reward.status === "pending" &&
                                  reward.spend_30_day !== undefined &&
                                  reward.spend_30_day !== null &&
                                  reward.spend_requirement
                                ) {
                                  // Both spend_30_day and spend_requirement are in Rands
                                  const spend30Day =
                                    typeof reward.spend_30_day === "string"
                                      ? parseFloat(reward.spend_30_day)
                                      : reward.spend_30_day;
                                  const spendRequirement =
                                    typeof reward.spend_requirement === "string"
                                      ? parseFloat(reward.spend_requirement)
                                      : reward.spend_requirement || 0;

                                  if (spend30Day < spendRequirement) {
                                    displayStatus = "spend not met";
                                    chipColor = "error";
                                  } else {
                                    chipColor = "warning";
                                  }
                                } else {
                                  // Use status color logic for all statuses
                                  chipColor =
                                    reward.status === "allocated"
                                      ? "success"
                                      : reward.status === "pending"
                                        ? "warning"
                                        : reward.status === "processing"
                                          ? "warning"
                                          : reward.status === "failed"
                                            ? "error"
                                            : reward.status === "expired"
                                              ? "error"
                                              : "default";
                                }

                                const statusBadge = (
                                  <Badge variant={CHIP_SEMANTIC_TO_BADGE[chipColor] ?? "secondary"}>
                                    {displayStatus}
                                  </Badge>
                                );

                                if (
                                  reward.status === "failed" &&
                                  reward.billing_failure_reason
                                ) {
                                  return (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger>
                                          {statusBadge}
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>{reward.billing_failure_reason}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  );
                                }

                                return statusBadge;
                              })()}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-row flex-wrap gap-1">
                                {rewardSources.length > 0 ? (
                                  rewardSources.map((source, idx) => (
                                    <Badge key={idx} variant="info">
                                      {source}
                                    </Badge>
                                  ))
                                ) : (
                                  <span className="text-xs text-muted-foreground">
                                    —
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="text-sm">
                                  {reward.spend_requirement
                                    ? `R${parseFloat(String(reward.spend_requirement)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    : "-"}
                                </p>
                                {reward.latest_spend_check_date ? (
                                  (() => {
                                    const date = new Date(
                                      reward.latest_spend_check_date,
                                    );
                                    const now = new Date();
                                    const diffMs =
                                      now.getTime() - date.getTime();
                                    const diffDays = Math.floor(
                                      diffMs / (1000 * 60 * 60 * 24),
                                    );

                                    const timeAgo = formatTimeAgoFromDate(
                                      reward.latest_spend_check_date,
                                    );
                                    const timestamp = new Date(
                                      reward.latest_spend_check_date,
                                    ).toLocaleString("en-US", {
                                      month: "2-digit",
                                      day: "2-digit",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    });

                                    let chipColor: "default" | "success" | "warning" | "error" = "default";
                                    if (diffDays >= 0 && diffDays < 7) {
                                      chipColor = "success";
                                    } else if (diffDays >= 7 && diffDays < 30) {
                                      chipColor = "warning";
                                    } else if (diffDays >= 30) {
                                      chipColor = "error";
                                    }

                                    return (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger>
                                            <Badge variant={CHIP_SEMANTIC_TO_BADGE[chipColor]} className="mt-1">
                                              {timeAgo}
                                            </Badge>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>{timestamp}</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    );
                                  })()
                                ) : (
                                  <Badge variant="secondary" className="mt-1">
                                    -
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              {reward.latest_created_at ? (
                                (() => {
                                  const date = new Date(
                                    reward.latest_created_at,
                                  );
                                  const now = new Date();
                                  const diffMs = now.getTime() - date.getTime();
                                  const diffDays = Math.floor(
                                    diffMs / (1000 * 60 * 60 * 24),
                                  );

                                  const timeAgo = formatTimeAgoFromDate(
                                    reward.latest_created_at,
                                  );
                                  const timestamp = new Date(
                                    reward.latest_created_at,
                                  ).toLocaleString("en-US", {
                                    month: "2-digit",
                                    day: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  });

                                  let chipColor: "default" | "success" | "warning" | "error" = "default";
                                  if (diffDays >= 0 && diffDays < 7) {
                                    chipColor = "success";
                                  } else if (diffDays >= 7 && diffDays < 30) {
                                    chipColor = "warning";
                                  } else if (diffDays >= 30) {
                                    chipColor = "error";
                                  }

                                  return (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <Badge variant={CHIP_SEMANTIC_TO_BADGE[chipColor]}>
                                            {timeAgo}
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>{timestamp}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  );
                                })()
                              ) : (
                                <Badge variant="secondary">
                                  -
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {reward.expires_at ? (
                                (() => {
                                  const date = new Date(reward.expires_at);
                                  const now = new Date();
                                  const diffMs = date.getTime() - now.getTime();
                                  const diffDays = Math.floor(
                                    diffMs / (1000 * 60 * 60 * 24),
                                  );

                                  const timeAgo = formatTimeAgoFromDate(
                                    reward.expires_at,
                                  );
                                  const timestamp = new Date(
                                    reward.expires_at,
                                  ).toLocaleString("en-US", {
                                    month: "2-digit",
                                    day: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  });

                                  let chipColor: "default" | "success" | "warning" | "error" = "default";
                                  if (diffMs < 0) {
                                    chipColor = "error";
                                  } else if (diffDays < 7) {
                                    chipColor = "warning";
                                  } else if (diffDays < 30) {
                                    chipColor = "success";
                                  } else {
                                    chipColor = "success";
                                  }

                                  return (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <Badge variant={CHIP_SEMANTIC_TO_BADGE[chipColor]}>
                                            {timeAgo}
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>{timestamp}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  );
                                })()
                              ) : (
                                <Badge variant="secondary">
                                  -
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => processReward(reward.i_customer)}
                                disabled={processing === reward.i_customer}
                              >
                                {processing === reward.i_customer
                                  ? "Processing..."
                                  : "Process"}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {pagination && (
                <div className="relative">
                  {loading && (
                    <div className="absolute inset-0 bg-white/50 z-5 rounded" />
                  )}
                  <div className="flex items-center justify-between px-2 py-4">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-muted-foreground">Rows per page</p>
                      <Select
                        value={String(rowsPerPage)}
                        onValueChange={(value) => {
                          if (value) handleChangeRowsPerPage({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
                        }}
                      >
                        <SelectTrigger className="h-8 w-[70px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[25, 50, 100, 200, 500].map((size) => (
                            <SelectItem key={size} value={String(size)}>
                              {size}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-muted-foreground">
                        Page {page + 1} of {Math.ceil(pagination.total / rowsPerPage)} ({pagination.total} total)
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleChangePage(null, 0)}
                        disabled={loading || page === 0}
                      >
                        First
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleChangePage(null, page - 1)}
                        disabled={loading || page === 0}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleChangePage(null, page + 1)}
                        disabled={loading || page >= Math.ceil(pagination.total / rowsPerPage) - 1}
                      >
                        Next
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleChangePage(null, Math.ceil(pagination.total / rowsPerPage) - 1)}
                        disabled={loading || page >= Math.ceil(pagination.total / rowsPerPage) - 1}
                      >
                        Last
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 p-4 bg-gray-50 rounded">
                <p className="text-sm">
                  <strong>Total:</strong>{" "}
                  {pagination?.total || pendingRewards.length} customers |{" "}
                  {formatMB(
                    pendingRewards.reduce(
                      (sum, r) =>
                        sum +
                        (typeof r.value === "string"
                          ? parseFloat(r.value)
                          : r.value),
                      0,
                    ),
                  )}{" "}
                  (showing page {pagination?.page || 1} of{" "}
                  {pagination?.pages || 1})
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Tab 2: SPAR Rewards */}
      {currentSection === "spar-rewards" && (
        <div className="p-6">
          <div className="flex justify-end items-center mb-6">
            <div className="flex flex-row gap-4">
              <Button
                variant="outline"
                onClick={loadFailedRewards}
                disabled={loadingFailed || processingAllFailed}
              >
                <RefreshIcon className="mr-2 h-4 w-4" />
                {loadingFailed ? "Refreshing..." : "Refresh"}
              </Button>
              <Button
                variant="default"
                onClick={processAllFailedRewards}
                disabled={
                  loadingFailed ||
                  processingAllFailed ||
                  filteredFailedRewards.length === 0
                }
              >
                <ProcessIcon className="mr-2 h-4 w-4" />
                {processingAllFailed
                  ? `Processing ${processProgressFailed.current}/${processProgressFailed.total}...`
                  : errorTypeFilter === "all"
                    ? `Process All (${filteredFailedRewards.length})`
                    : `Process ${errorTypeFilter} (${filteredFailedRewards.length})`}
              </Button>
              <Button
                variant="outline"
                onClick={downloadErrorsCSV}
                disabled={loadingFailed || filteredFailedRewards.length === 0}
              >
                <DownloadIcon className="mr-2 h-4 w-4" />
                Download CSV ({filteredFailedRewards.length})
              </Button>
            </div>
          </div>

          {/* Statistics Dashboard */}
          {!loadingFailed && failedRewards.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-muted-foreground mb-2">
                Click on a card to filter by error type
              </p>
              <div className="flex flex-wrap gap-4">
                {/* All Errors Card */}
                <div
                  onClick={() => setErrorTypeFilter("all")}
                  className={cn(
                    "flex-1 min-w-[150px] p-4 rounded-lg bg-slate-100 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md",
                    errorTypeFilter === "all"
                      ? "border-2 border-primary"
                      : "border border-transparent"
                  )}
                >
                  <span className="text-xs text-muted-foreground">
                    All Errors
                  </span>
                  <h1 className="text-3xl font-bold mt-1">
                    {failedRewards.length}
                  </h1>
                </div>

                {/* Individual Error Type Cards */}
                {Object.entries(errorStats).map(([category, count]) => (
                  <div
                    key={category}
                    onClick={() => setErrorTypeFilter(category)}
                    className={cn(
                      "flex-1 min-w-[150px] p-4 rounded-lg bg-slate-100 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md",
                      errorTypeFilter === category
                        ? "border-2 border-primary"
                        : "border border-transparent"
                    )}
                  >
                    <span className="text-xs text-muted-foreground">
                      {category}
                    </span>
                    <h2 className="text-2xl font-bold mt-1">
                      {count}
                    </h2>
                    <span className="text-xs text-muted-foreground block mt-1">
                      {Math.round((count / failedRewards.length) * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Filter Indicator */}
          {!loadingFailed &&
            failedRewards.length > 0 &&
            errorTypeFilter !== "all" && (
              <Alert className="mb-4 border-blue-200 bg-blue-50 text-blue-800">
                <AlertDescription className="flex items-center justify-between">
                  <span>
                    <strong>Filtered View:</strong> Showing only "{errorTypeFilter}"
                    errors ({filteredFailedRewards.length} of {failedRewards.length}{" "}
                    total)
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setErrorTypeFilter("all")}
                  >
                    Clear Filter
                  </Button>
                </AlertDescription>
              </Alert>
            )}

          {errorFailed && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{errorFailed}</AlertDescription>
            </Alert>
          )}
          {successFailed && (
            <Alert className="mb-4 border-green-200 bg-green-50 text-green-800">
              <AlertDescription>{successFailed}</AlertDescription>
            </Alert>
          )}

          {loadingFailed && failedRewards.length === 0 ? (
            <div className="flex justify-center py-8">
              <IconLoader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : failedRewards.length === 0 ? (
            <Alert className="border-blue-200 bg-blue-50 text-blue-800">
              <AlertDescription>No failed registrations found</AlertDescription>
            </Alert>
          ) : (
            <>
              <Alert className="mb-4 border-yellow-200 bg-yellow-50 text-yellow-800">
                <AlertDescription>
                  {errorTypeFilter === "all"
                    ? `Showing all ${failedRewards.length} failed SPAR registrations`
                    : `Showing ${filteredFailedRewards.length} of ${failedRewards.length} (filtered by: ${errorTypeFilter})`}
                </AlertDescription>
              </Alert>

              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer ID</TableHead>
                      <TableHead>MSISDN</TableHead>
                      <TableHead>ID Number</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Error</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFailedRewards.map((record) => (
                      <TableRow key={record.id} className="hover:bg-muted/50">
                        <TableCell>{record.i_customer}</TableCell>
                        <TableCell>
                          {record.msisdn_dma ||
                            record.msisdn_churned ||
                            record.msisdn ||
                            "-"}
                        </TableCell>
                        <TableCell>{record.id_number}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {record.email || "-"}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() =>
                                openEmailModal(record.id, record.email || "")
                              }
                            >
                              <EditIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-red-600 max-w-[300px]">
                          {parseErrorMessage(record.spar_uuid_failed_error)}
                        </TableCell>
                        <TableCell>
                          {new Date(record.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => processFailedReward(record.id)}
                            disabled={processingFailed === record.id}
                          >
                            {processingFailed === record.id
                              ? "Processing..."
                              : "Process"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 p-4 bg-gray-50 rounded">
                <p className="text-sm">
                  <strong>Total Failed Registrations:</strong>{" "}
                  {failedRewards.length}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Tab 3: ROM Management */}
      {currentSection === "rom-management" && (
        <div className="p-6">
          {storeError && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>{storeError}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setStoreError("")}
                >
                  <IconX className="size-4" />
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {storeSuccess && (
            <Alert className="border-green-200 bg-green-50 text-green-800 mb-4">
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>{storeSuccess}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setStoreSuccess("")}
                >
                  <IconX className="size-4" />
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {notFoundCodes.length > 0 && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>
                <p className="text-sm font-medium mb-2">
                  Could not find {notFoundCodes.length} store
                  {notFoundCodes.length === 1 ? "" : "s"} in SPAR hierarchy:
                </p>
                <div className="flex flex-wrap gap-1">
                  {notFoundCodes.map((code) => (
                    <Badge key={code} variant="destructive">
                      {code}
                    </Badge>
                  ))}
                </div>
              </AlertDescription>
            </Alert>
          )}
          {!romOptionsLoading &&
            romOptions.length === 0 &&
            storeHasSearched && (
              <Alert className="border-yellow-200 bg-yellow-50 text-yellow-800 mb-4">
                <AlertDescription>
                  No ROM options available. Please try again later.
                </AlertDescription>
              </Alert>
            )}

          <MainCard className="mb-6">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-base font-semibold">Search Store</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Find a SPAR store by name, external ID, or CyclePay terminal
                  ID to view and manage its ROM assignment.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => ensureRomOptions(true)}
                disabled={romOptionsLoading}
              >
                <RefreshIcon
                  className={cn(
                    "size-4",
                    romOptionsLoading && "animate-spin",
                  )}
                />
                {romOptionsLoading ? "Refreshing ROMs..." : "Refresh ROMs"}
              </Button>
            </div>

            {selectedStores.length > 0 && (
              <div className="mb-4 rounded-lg border border-dashed border-muted-foreground/40 bg-muted/30 p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-muted-foreground">
                    Selected ({selectedStores.length})
                  </span>
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => handleStoreSelect(null, [])}
                  >
                    Clear all
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {selectedStores.map((store) => (
                    <Badge
                      key={store.id}
                      variant="secondary"
                      className="gap-1"
                    >
                      {store.name}
                      <button
                        type="button"
                        className="ml-1 rounded hover:text-destructive"
                        onClick={() => {
                          const newSelection = selectedStores.filter(
                            (s) => s.id !== store.id,
                          );
                          handleStoreSelect(null, newSelection);
                        }}
                      >
                        <IconX className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="store-search-input">
                Search by name, external ID, or terminal ID
              </Label>
              <Popover
                open={
                  storeSearchTerm.trim().length >= 2 &&
                  storeSuggestions.length > 0
                }
                onOpenChange={() => {}}
              >
                <PopoverTrigger nativeButton={false} render={<div className="relative" />}>
                    <IconSearch className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="store-search-input"
                      className="pl-9"
                      placeholder="Start typing store name, external ID, or terminal ID..."
                      value={storeSearchTerm}
                      onChange={(e) =>
                        handleStoreInputChange(
                          null,
                          e.target.value,
                          e.target.value ? "input" : "clear",
                        )
                      }
                    />
                    {storeLoading && (
                      <IconLoader2 className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                    )}
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                  initialFocus={false}
                >
                  <Command shouldFilter={false}>
                    <CommandList>
                      {storeLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <IconLoader2 className="h-4 w-4 animate-spin" />
                          <span className="ml-2 text-sm">
                            Searching stores...
                          </span>
                        </div>
                      ) : storeSuggestions.length === 0 ? (
                        <CommandEmpty>
                          {storeSearchTerm.trim().length < 2
                            ? "Type at least 2 characters"
                            : "No stores found"}
                        </CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {storeAutocompleteOptions
                            .filter(
                              (option) =>
                                !selectedStores.some((s) => s.id === option.id),
                            )
                            .map((option) => (
                              <CommandItem
                                key={option.id}
                                value={String(option.id)}
                                onSelect={() => {
                                  handleStoreSelect(null, [
                                    ...selectedStores,
                                    option,
                                  ]);
                                  setStoreSearchTerm("");
                                }}
                              >
                                <div>
                                  <p className="text-sm font-bold">
                                    {option.name}
                                  </p>
                                  <span className="text-xs text-muted-foreground">
                                    {option.external_id || "No external ID"}
                                    {option.cyclepay_terminal_id
                                      ? ` • Terminal: ${option.cyclepay_terminal_id}`
                                      : ""}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                Type at least 2 characters. Pick a suggestion to add it to the
                results below, or keep typing to refine your search.
              </p>
            </div>
          </MainCard>

          {storeLoading && stores.length === 0 ? (
            <MainCard>
              <div className="flex justify-center py-12">
                <IconLoader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            </MainCard>
          ) : stores.length === 0 ? (
            <MainCard>
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <IconSearch className="mb-3 size-10 text-muted-foreground/40" />
                <p className="text-sm font-medium">
                  {storeHasSearched
                    ? `No stores matched "${trimmedStoreSearch}"`
                    : "Search for a store to get started"}
                </p>
                <p className="mt-1 max-w-sm text-xs text-muted-foreground">
                  {storeHasSearched
                    ? "Try a different name, external ID, or CyclePay terminal ID."
                    : "Use the search box above to find SPAR Mobile stores by name, external ID, or CyclePay terminal ID. Selected stores will appear here so you can update their ROM assignments."}
                </p>
              </div>
            </MainCard>
          ) : (
            <MainCard>
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold">Stores</h3>
                <span className="text-xs text-muted-foreground">
                  {selectedStores.length > 0
                    ? `Showing ${selectedStores.length} selected store${selectedStores.length === 1 ? "" : "s"}`
                    : trimmedStoreSearch.length >= 2
                      ? `Showing ${stores.length} store${stores.length === 1 ? "" : "s"} for "${trimmedStoreSearch}"`
                      : `Showing ${stores.length} store${stores.length === 1 ? "" : "s"}`}
                </span>
              </div>
              <Separator className="mb-3" />

              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>External ID</TableHead>
                      <TableHead>Store</TableHead>
                      <TableHead>DC</TableHead>
                      <TableHead>Current ROM</TableHead>
                      <TableHead>Select ROM</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stores.map((store) => {
                      const currentSelection = selectedRomMap[store.id] ?? "";
                      const isUpdating = !!updatingStores[store.id];
                      const originalRomId = store.rom_id ?? null;
                      const hasChange =
                        typeof currentSelection === "number" &&
                        currentSelection !== originalRomId;
                      const contactName = [
                        store.contact_first_name,
                        store.contact_last_name,
                      ]
                        .filter(Boolean)
                        .join(" ");
                      const contactDetails = [
                        contactName,
                        store.contact_phone_number,
                        store.contact_email_address,
                      ]
                        .filter(Boolean)
                        .join(" • ");

                      return (
                        <TableRow key={store.id} className="hover:bg-muted/50">
                          <TableCell>
                            <p className="text-sm">{store.id}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">
                              {store.external_id || "—"}
                            </p>
                            {store.cyclepay_terminal_id && (
                              <span className="text-xs text-muted-foreground block">
                                Terminal: {store.cyclepay_terminal_id}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <p className="text-base font-medium font-bold">
                              {store.name}
                            </p>
                            {store.address_line_1 && (
                              <p className="text-sm text-muted-foreground">
                                {store.address_line_1}
                                {store.city ? `, ${store.city}` : ""}
                              </p>
                            )}
                            {contactDetails && (
                              <p className="text-sm text-muted-foreground">
                                Contact: {contactDetails}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">
                              {store.dc_name || "—"}
                            </p>
                          </TableCell>
                          <TableCell>
                            {store.rom_name ? (
                              <>
                                <p className="text-sm font-bold">
                                  {store.rom_name}
                                </p>
                                {store.rom_contact_email && (
                                  <span className="text-xs text-muted-foreground block">
                                    {store.rom_contact_email}
                                  </span>
                                )}
                                {store.rom_contact_phone_number && (
                                  <span className="text-xs text-muted-foreground block">
                                    {store.rom_contact_phone_number}
                                  </span>
                                )}
                              </>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                No ROM assigned
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="min-w-[220px]">
                            <Popover>
                              <PopoverTrigger
                                render={
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    className="w-full justify-between text-left font-normal"
                                    disabled={romOptions.length === 0}
                                  />
                                }
                              >
                                  {typeof currentSelection === "number"
                                    ? romOptions.find((rom) => rom.id === currentSelection)?.name || `ROM ${currentSelection}`
                                    : "Choose ROM"}
                                  <IconChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </PopoverTrigger>
                              <PopoverContent className="w-[250px] p-0">
                                <Command>
                                  <CommandInput placeholder="Search ROM..." />
                                  <CommandList>
                                    <CommandEmpty>{romOptionsLoading ? "Loading ROMs..." : "No ROM options available"}</CommandEmpty>
                                    <CommandGroup>
                                      {romOptions.map((rom) => (
                                        <CommandItem
                                          key={rom.id}
                                          value={rom.name || `ROM ${rom.id}`}
                                          onSelect={() => handleRomSelectionChange(store.id, rom)}
                                        >
                                          <IconCheck className={cn("mr-2 h-4 w-4", currentSelection === rom.id ? "opacity-100" : "opacity-0")} />
                                          {rom.name || `ROM ${rom.id}${rom.dc_name ? ` (${rom.dc_name})` : ""}`}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              onClick={() => handleUpdateStoreRom(store.id)}
                              disabled={
                                isUpdating ||
                                romOptions.length === 0 ||
                                typeof currentSelection !== "number" ||
                                !hasChange
                              }
                            >
                              <EditIcon className="mr-2 h-4 w-4" />
                              {isUpdating ? "Updating..." : "Update"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </MainCard>
          )}
        </div>
      )}

      {/* Store Ownership Change */}
      {currentSection === "store-ownership-change" && (
        <StoreOwnershipChangePanel />
      )}

      {/* Tab 4: Voucher Matching */}
      {currentSection === "voucher-matching" && (
        <div className="p-6">
          <RetailerStoreUploadCard />
          <MainCard className="p-6 mb-6">
            <div className="space-y-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="min-w-[200px]">
                  <Label>Month</Label>
                  <Input
                    type="month"
                    value={matchingMonth}
                    onChange={(e) => setMatchingMonth(e.target.value)}
                  />
                </div>
                <div className="flex-1">
                  <Label>Voucher state filter</Label>
                  <Select value={unmatchedStateFilter} onValueChange={(val) => { if (val) setUnmatchedStateFilter(val); }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {unmatchedStateOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option === "ANY"
                            ? "All states"
                            : option.charAt(0) + option.slice(1).toLowerCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <Label>Markup status filter</Label>
                  <Select value={unmatchedMarkupFilter} onValueChange={(val) => { if (val) setUnmatchedMarkupFilter(val); }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {unmatchedMarkupOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option === "ANY"
                            ? "Any"
                            : option === "NULL"
                              ? "Missing"
                              : option.charAt(0) + option.slice(1).toLowerCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label>Matched status filter</Label>
                  <Select value={unmatchedMatchedFilter} onValueChange={(val) => { if (val) setUnmatchedMatchedFilter(val); }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {unmatchedMatchedOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option === "ANY"
                            ? "Any"
                            : option === "NULL"
                              ? "Missing"
                              : option.charAt(0) + option.slice(1).toLowerCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label>Max rows</Label>
                  <Select value={String(unmatchedLimit)} onValueChange={(val) => { if (val) setUnmatchedLimit(Number(val)); }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {unmatchedLimitOptions.map((option) => (
                        <SelectItem key={option} value={String(option)}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={onlyProblematic}
                    onCheckedChange={(checked) =>
                      setOnlyProblematic(checked as boolean)
                    }
                  />
                  <Label>Only show vouchers with mismatched statuses</Label>
                </div>
                <div className="flex-grow" />
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      void Promise.all([
                        loadVoucherMatchingSummary(),
                        loadVoucherRunStatuses(),
                      ]);
                    }}
                    disabled={matchingLoading || fixingReversed}
                  >
                    <RefreshIcon className="mr-2 h-4 w-4" />
                    {matchingLoading
                      ? "Refreshing summary..."
                      : "Refresh summary"}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      void refreshVoucherMatchingData();
                    }}
                    disabled={
                      unmatchedLoading || fixingReversed || matchingLoading
                    }
                  >
                    <UnmatchedIcon className="mr-2 h-4 w-4" />
                    {unmatchedLoading || matchingLoading
                      ? "Refreshing..."
                      : "Refresh vouchers"}
                  </Button>
                </div>
              </div>
            </div>
          </MainCard>

          {matchingError && (
            <div className="relative mb-4">
              <Alert variant="destructive">
                <AlertDescription>{matchingError}</AlertDescription>
                <button
                  type="button"
                  className="absolute right-2 top-2 rounded-md p-1 hover:bg-muted"
                  onClick={() => setMatchingError("")}
                >
                  <IconX className="h-4 w-4" />
                </button>
              </Alert>
            </div>
          )}
          {matchingSuccess && (
            <div className="relative mb-4">
              <Alert className="border-green-200 bg-green-50 text-green-800">
                <AlertDescription>{matchingSuccess}</AlertDescription>
                <button
                  type="button"
                  className="absolute right-2 top-2 rounded-md p-1 hover:bg-muted"
                  onClick={() => setMatchingSuccess("")}
                >
                  <IconX className="h-4 w-4" />
                </button>
              </Alert>
            </div>
          )}
          {unmatchedError && (
            <div className="relative mb-4">
              <Alert variant="destructive">
                <AlertDescription>{unmatchedError}</AlertDescription>
                <button
                  type="button"
                  className="absolute right-2 top-2 rounded-md p-1 hover:bg-muted"
                  onClick={() => setUnmatchedError("")}
                >
                  <IconX className="h-4 w-4" />
                </button>
              </Alert>
            </div>
          )}

          {matchingLoading && !matchingSummary ? (
            <div className="flex justify-center py-8">
              <IconLoader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : matchingSummary ? (
            <>
              {(() => {
                const severity = cronStatusToSeverity(matchingSummary.alerts.cronStatus);
                const severityClasses = severity === "error"
                  ? "border-red-200 bg-red-50 text-red-800"
                  : severity === "warning"
                    ? "border-yellow-200 bg-yellow-50 text-yellow-800"
                    : severity === "success"
                      ? "border-green-200 bg-green-50 text-green-800"
                      : "border-blue-200 bg-blue-50 text-blue-800";
                return (
                  <Alert className={`${severityClasses} mb-6`}>
                    <AlertDescription>
                      <strong>Daily cron:</strong>{" "}
                      {matchingSummary.alerts.cronMessage}
                      <br />
                      <strong>Latest matched:</strong>{" "}
                      {formatDateTimeDisplay(matchingSummary.alerts.latestMatchedAt)}
                    </AlertDescription>
                  </Alert>
                );
              })()}

              {matchingSummary.alerts.missingMatchDays.length > 0 && (
                <Alert className="border-yellow-200 bg-yellow-50 text-yellow-800 mb-4">
                  <AlertDescription>
                    Missing matching for:{" "}
                    {matchingSummary.alerts.missingMatchDays
                      .map((day) => formatDateDisplay(day))
                      .join(", ")}
                  </AlertDescription>
                </Alert>
              )}
              {matchingSummary.alerts.missingMarkupDays.length > 0 && (
                <Alert className="border-yellow-200 bg-yellow-50 text-yellow-800 mb-4">
                  <AlertDescription>
                    Missing markup updates for:{" "}
                    {matchingSummary.alerts.missingMarkupDays
                      .map((day) => formatDateDisplay(day))
                      .join(", ")}
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex flex-col md:flex-row gap-4 mb-6 flex-wrap">
                {voucherSummaryCards.map((card) => (
                  <div
                    key={card.key}
                    className="flex-1 p-4 rounded-lg border border-border min-w-[180px]"
                  >
                    <span className="text-xs text-muted-foreground">
                      {card.label}
                    </span>
                    <h2
                      className="text-xl font-bold mt-1"
                      style={card.color ? { color: card.color } : undefined}
                    >
                      {formatInteger(card.value)}
                    </h2>
                    {card.helper && (
                      <span className="text-xs text-muted-foreground">
                        {card.helper}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <div className="mb-6">
                <p className="text-base font-medium flex items-center gap-1 mb-2">
                  <TimelineIcon className="h-4 w-4" />
                  Daily matching
                </p>
                {processStatusError && (
                  <Alert variant="destructive" className="mb-3">
                    <AlertDescription>
                      {processStatusError}. Reconciliation actions are disabled
                      until status is available.
                    </AlertDescription>
                  </Alert>
                )}
                {matchingSummary.dailyStats.length === 0 ? (
                  <Alert className="border-blue-200 bg-blue-50 text-blue-800">
                    <AlertDescription>No vouchers found in the selected range.</AlertDescription>
                  </Alert>
                ) : (
                  <div className="overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-right">Markup matched</TableHead>
                          <TableHead className="text-right">
                            Markup outstanding
                          </TableHead>
                          <TableHead className="text-right">Matched</TableHead>
                          <TableHead className="text-right">Unmatched</TableHead>
                          <TableHead>Last matched at</TableHead>
                          <TableHead className="text-center min-w-[140px]">
                            Actions
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {matchingSummary.dailyStats.map((row) => {
                          const highlight =
                            row.total > 0 &&
                            (row.matched === 0 || row.markupMatched === 0);
                          return (
                            <TableRow
                              key={row.day}
                              className={cn(
                                "hover:bg-muted/50",
                                highlight && "bg-yellow-50/50"
                              )}
                            >
                              <TableCell>
                                {formatDateDisplay(row.day)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatInteger(row.total)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatInteger(row.markupMatched)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatInteger(row.markupUnmatched)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatInteger(row.matched)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatInteger(row.unmatched)}
                              </TableCell>
                              <TableCell>
                                {formatDateTimeDisplay(row.latestMatchedAt)}
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex gap-1 justify-center">
                                  {([
                                    {
                                      type: "markup",
                                      icon: MarkupIcon,
                                      label: "Markup",
                                    },
                                    {
                                      type: "matched",
                                      icon: MatchedIcon,
                                      label: "Matched",
                                    },
                                  ] as const).map(({ type, icon: Icon, label }) => {
                                    const dayKey = formatDate(row.day);
                                    const status =
                                      processStatuses[dayKey]?.[type];
                                    const actionState =
                                      getVoucherRunActionState({
                                        availability:
                                          processStatusAvailability,
                                        run: status,
                                      });
                                    const running =
                                      runningProcess?.type === type &&
                                      runningProcess?.date === dayKey;

                                    return (
                                      <TooltipProvider key={type}>
                                        <Tooltip>
                                          <TooltipTrigger
                                            render={
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className={cn(
                                                  "h-8 w-8",
                                                  status?.status === "SUCCESS" &&
                                                    "text-green-600",
                                                  status?.status === "FAILED" &&
                                                    "text-red-600",
                                                  status?.status ===
                                                    "MISSING_FILE" &&
                                                    "text-yellow-600",
                                                  status?.status === "NOT_RUN" &&
                                                    "text-primary",
                                                  running && "opacity-60"
                                                )}
                                                aria-label={`${label}: ${actionState.label}`}
                                                onClick={() =>
                                                  handleRunProcess(type, dayKey)
                                                }
                                                disabled={
                                                  running ||
                                                  matchingLoading ||
                                                  actionState.disabled
                                                }
                                              />
                                            }
                                          >
                                              {running ? (
                                                <IconLoader2 className="h-4 w-4 animate-spin" />
                                              ) : (
                                                <Icon className="h-4 w-4" />
                                              )}
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>
                                              {running
                                                ? `Running ${label}...`
                                                : `${label}: ${actionState.label}${status?.lastAttemptAt ? ` (last attempt ${new Date(status.lastAttemptAt).toLocaleString()})` : ""}`}
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    );
                                  })}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              <Separator className="my-6" />

              <div className="mb-6">
                <p className="text-base font-medium mb-2">
                  Markup comparison
                </p>
                <div className="overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>State</TableHead>
                        <TableHead>Markup state</TableHead>
                        <TableHead>Markup status</TableHead>
                        <TableHead className="text-right">Vouchers</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {matchingSummary.markupBreakdown
                        .filter((row) => row.markup_status !== "MATCHED")
                        .map((row, index) => {
                          const highlight = row.markup_status !== "MATCHED";
                          return (
                            <TableRow
                              key={`${row.state || "NULL"}-${row.markup_state || "NULL"}-${row.markup_status || "NULL"}-${index}`}
                              className={cn(
                                "hover:bg-muted/50",
                                highlight && "bg-yellow-50/50"
                              )}
                            >
                              <TableCell>{row.state || "—"}</TableCell>
                              <TableCell>{row.markup_state || "—"}</TableCell>
                              <TableCell>{row.markup_status || "—"}</TableCell>
                              <TableCell className="text-right">
                                {formatInteger(row.count)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-base font-medium mb-2">
                  Matching comparison
                </p>
                <div className="overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>State</TableHead>
                        <TableHead>Markup state</TableHead>
                        <TableHead>Matched status</TableHead>
                        <TableHead className="text-right">Vouchers</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {matchingSummary.matchingBreakdown
                        .filter((row) => row.matched_status !== "MATCHED")
                        .map((row, index) => {
                          const highlight = row.matched_status !== "MATCHED";
                          return (
                            <TableRow
                              key={`${row.state || "NULL"}-${row.markup_state || "NULL"}-${row.matched_status || "NULL"}-${index}`}
                              className={cn(
                                "hover:bg-muted/50",
                                highlight && "bg-red-50/20"
                              )}
                            >
                              <TableCell>{row.state || "—"}</TableCell>
                              <TableCell>{row.markup_state || "—"}</TableCell>
                              <TableCell>{row.matched_status || "—"}</TableCell>
                              <TableCell className="text-right">
                                {formatInteger(row.count)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="mb-8">
                <p className="text-sm font-medium text-muted-foreground mb-2">
                  Voucher states in range
                </p>
                <div className="flex flex-row gap-2 flex-wrap">
                  {matchingSummary.stateBreakdown.map((row) => (
                    <Badge
                      key={row.state || "null"}
                      variant="secondary"
                    >
                      {`${row.state || "Unknown"} • ${formatInteger(row.count)}`}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <p className="text-base font-medium flex items-center gap-1 mb-2">
                  <QuickFixIcon className="h-4 w-4" />
                  Quick fixes
                </p>
                <p className="text-sm text-muted-foreground mb-2">
                  Apply known-safe adjustments for the selected period.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="default"
                    onClick={() => {
                      void fixReversedTransactions();
                    }}
                    disabled={
                      fixingReversed || matchingLoading || unmatchedLoading
                    }
                  >
                    <QuickFixIcon className="mr-2 h-4 w-4" />
                    {fixingReversed
                      ? "Fixing reversed..."
                      : "Fix reversed transactions"}
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => {
                      void fixAuthorisedTransactions();
                    }}
                    disabled={
                      fixingAuthorised || matchingLoading || unmatchedLoading
                    }
                  >
                    <QuickFixIcon className="mr-2 h-4 w-4" />
                    {fixingAuthorised
                      ? "Fixing authorised..."
                      : "Fix authorised transactions"}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <Alert className="border-blue-200 bg-blue-50 text-blue-800 mb-6">
              <AlertDescription>
                Select a date range and click refresh to load voucher matching
                statistics.
              </AlertDescription>
            </Alert>
          )}

          <Separator className="my-6" />

          <h3 className="text-lg font-semibold flex items-center gap-1 mb-4">
            <UnmatchedIcon className="h-5 w-5" />
            Investigate vouchers
          </h3>

          {unmatchedLoading && unmatchedVouchers.length === 0 ? (
            <div className="flex justify-center py-8">
              <IconLoader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : unmatchedVouchers.length === 0 ? (
            <Alert className="border-blue-200 bg-blue-50 text-blue-800">
              <AlertDescription>No vouchers matched the current filters.</AlertDescription>
            </Alert>
          ) : (
            <>
              {unmatchedMeta && (
                <Alert className="border-blue-200 bg-blue-50 text-blue-800 mb-4">
                  <AlertDescription>
                    Showing {unmatchedVouchers.length} voucher
                    {unmatchedVouchers.length === 1 ? "" : "s"} (max{" "}
                    {unmatchedMeta.limit}
                    {unmatchedMeta.range
                      ? ` for ${formatDateDisplay(unmatchedMeta.range.start)} → ${formatDateDisplay(unmatchedMeta.range.end)}`
                      : ""}
                    )
                  </AlertDescription>
                </Alert>
              )}
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Voucher</TableHead>
                      <TableHead>MSISDN</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Markup state</TableHead>
                      <TableHead>Markup status</TableHead>
                      <TableHead>Matched status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Matched at</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {unmatchedVouchers.map((voucher, index) => {
                      const identifier = getVoucherIdentifier(voucher);
                      const msisdn = getVoucherMsisdn(voucher);
                      const amount = getVoucherValue(voucher);
                      const createdAt = voucher.created_at || voucher.createdAt;
                      const matchedAt = voucher.matched_at || voucher.matchedAt;
                      const highlight =
                        voucher.state === "CONFIRMED" &&
                        voucher.matched_status !== "MATCHED";
                      return (
                        <TableRow
                          key={voucher.id || identifier || index}
                          className={cn(
                            "hover:bg-muted/50",
                            highlight && "bg-red-50/20"
                          )}
                        >
                          <TableCell>
                            <p className="text-sm font-bold">
                              {identifier}
                            </p>
                            <span className="text-xs text-muted-foreground block">
                              Customer: {voucher.i_customer || "—"}
                            </span>
                          </TableCell>
                          <TableCell>{msisdn || "—"}</TableCell>
                          <TableCell className="text-right">
                            {amount !== null && amount !== undefined
                              ? amount
                              : "—"}
                          </TableCell>
                          <TableCell>{voucher.state || "—"}</TableCell>
                          <TableCell>{voucher.markup_state || "—"}</TableCell>
                          <TableCell>{voucher.markup_status || "—"}</TableCell>
                          <TableCell>{voucher.matched_status || "—"}</TableCell>
                          <TableCell>
                            {formatDateTimeDisplay(createdAt)}
                          </TableCell>
                          <TableCell>
                            {formatDateTimeDisplay(matchedAt)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Tab 5: Incentive Reconciliation */}
      {currentSection === "incentive-reconciliation" && (
        <div className="p-6">
          <MainCard className="p-6 mb-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                {viewMode === "monthly" ? (
                  <>
                    <div className="min-w-[200px]">
                      <Label>Month</Label>
                      <Input
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                      />
                    </div>
                    <Button
                      onClick={() => loadMonthlySummary(selectedMonth)}
                      disabled={monthlyLoading || !selectedMonth}
                    >
                      {monthlyLoading ? (
                        <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshIcon className="mr-2 h-4 w-4" />
                      )}
                      {monthlyLoading ? "Loading..." : "Load Monthly Summary"}
                    </Button>
                    {calculating && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <IconLoader2 className="h-4 w-4 animate-spin" />
                        <p className="text-sm">
                          Calculating reconciliation...
                        </p>
                      </div>
                    )}
                    {reconciliationData && (
                      <Button
                        variant="outline"
                        onClick={() => setViewMode("daily")}
                      >
                        View Daily Details
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="min-w-[200px]">
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={reconciliationDate}
                        onChange={(e) => setReconciliationDate(e.target.value)}
                      />
                    </div>
                    <Button
                      onClick={() => loadReconciliation(reconciliationDate)}
                      disabled={reconciliationLoading || !reconciliationDate}
                    >
                      {reconciliationLoading ? (
                        <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshIcon className="mr-2 h-4 w-4" />
                      )}
                      {reconciliationLoading
                        ? "Loading..."
                        : "Load Daily Details"}
                    </Button>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger render={<span className="inline-block" />}>
                          <Button
                              onClick={() =>
                                allocateIncentiveData(reconciliationDate)
                              }
                              disabled={
                                allocating ||
                                !reconciliationDate ||
                                (reconciliationData?.totals?.allocation_count ??
                                  0) > 0
                              }
                            >
                              {allocating ? (
                                <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <ProcessIcon className="mr-2 h-4 w-4" />
                              )}
                              {allocating
                                ? "Allocating..."
                                : "Allocate Incentive Data"}
                            </Button>
                        </TooltipTrigger>
                        {(reconciliationData?.totals?.allocation_count ?? 0) > 0 && (
                          <TooltipContent>
                            <p>Incentive data for this date has already been allocated.</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setViewMode("monthly");
                        setReconciliationData(null);
                        if (selectedMonth) {
                          loadMonthlySummary(selectedMonth);
                        }
                      }}
                    >
                      Back to Monthly View
                    </Button>
                  </>
                )}
              </div>
            </div>
          </MainCard>

          {(monthlyError ||
            reconciliationError ||
            calculationError ||
            allocationError) && (
            <div className="relative mb-6">
              <Alert variant="destructive">
                <AlertDescription>
                  {allocationError ||
                    calculationError ||
                    (viewMode === "monthly" ? monthlyError : reconciliationError)}
                </AlertDescription>
              </Alert>
              <button
                type="button"
                className="absolute right-2 top-2 rounded-md p-1 hover:bg-muted"
                onClick={() => {
                  setMonthlyError("");
                  setReconciliationError("");
                  setCalculationError("");
                  setAllocationError("");
                }}
              >
                <IconX className="h-4 w-4" />
              </button>
            </div>
          )}

          {(calculationSuccess || allocationSuccess) && (
            <div className="relative mb-6">
              <Alert className="border-green-200 bg-green-50 text-green-800">
                <AlertDescription>
                  {allocationSuccess || calculationSuccess}
                </AlertDescription>
              </Alert>
              <button
                type="button"
                className="absolute right-2 top-2 rounded-md p-1 hover:bg-muted"
                onClick={() => {
                  setCalculationSuccess("");
                  setAllocationSuccess("");
                }}
              >
                <IconX className="h-4 w-4" />
              </button>
            </div>
          )}

          {viewMode === "monthly" ? (
            <>
              {monthlyLoading || calculating ? (
                <div className="flex flex-col items-center justify-center p-8">
                  <IconLoader2 className="h-6 w-6 animate-spin" />
                  <p className="text-sm text-muted-foreground mt-4">
                    {calculating
                      ? "Calculating reconciliation data..."
                      : "Loading..."}
                  </p>
                </div>
              ) : monthlySummary ? (
                <>
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2">
                      Monthly Summary - {monthlySummary.month_name}{" "}
                      {monthlySummary.year}
                    </h3>
                    <div className="flex flex-col md:flex-row gap-4 mt-4 flex-wrap">
                      <div className="flex-1 p-4 rounded-lg border min-w-[180px]">
                        <span className="text-xs text-muted-foreground">
                          Total SIM Cards Scanned
                        </span>
                        <h2 className="text-xl font-bold mt-1">
                          {formatInteger(
                            monthlySummary.totals.sim_cards_scanned,
                          )}
                        </h2>
                      </div>
                      <div className="flex-1 p-4 rounded-lg border min-w-[180px]">
                        <span className="text-xs text-muted-foreground">
                          Total Boxes
                        </span>
                        <h2 className="text-xl font-bold mt-1">
                          {formatDecimal(monthlySummary.totals.boxes)}
                        </h2>
                      </div>
                      <div className="flex-1 p-4 rounded-lg border min-w-[180px]">
                        <span className="text-xs text-muted-foreground">
                          Expected Incentive Data
                        </span>
                        <h2 className="text-xl font-bold text-primary mt-1">
                          {formatDecimal(
                            monthlySummary.totals.expected_incentive_gb,
                          )}{" "}
                          GB
                        </h2>
                      </div>
                      <div className="flex-1 p-4 rounded-lg border min-w-[180px]">
                        <span className="text-xs text-muted-foreground">
                          Allocated Data
                        </span>
                        <h2 className="text-xl font-bold text-green-600 mt-1">
                          {formatDecimal(monthlySummary.totals.allocated_gb)} GB
                        </h2>
                      </div>
                      <div className="flex-1 p-4 rounded-lg border min-w-[180px]">
                        <span className="text-xs text-muted-foreground">
                          Difference
                        </span>
                        <h2 className={cn("text-xl font-bold mt-1",
                          Math.abs(monthlySummary.totals.difference_gb) < 0.01
                            ? ""
                            : monthlySummary.totals.difference_gb > 0
                              ? "text-red-600"
                              : "text-yellow-600"
                        )}>
                          {monthlySummary.totals.difference_gb >= 0 ? "+" : ""}
                          {formatDecimal(
                            monthlySummary.totals.difference_gb,
                          )}{" "}
                          GB
                        </h2>
                        <span className="text-xs text-muted-foreground">
                          {monthlySummary.totals.difference_percentage >= 0
                            ? "+"
                            : ""}
                          {formatDecimal(
                            monthlySummary.totals.difference_percentage,
                          )}
                          %
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">SIM Cards</TableHead>
                          <TableHead className="text-right">Boxes</TableHead>
                          <TableHead className="text-right">Expected (GB)</TableHead>
                          <TableHead className="text-right">Allocated (GB)</TableHead>
                          <TableHead className="text-right">Difference (GB)</TableHead>
                          <TableHead className="text-right">Difference %</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {monthlySummary.days
                          .filter((day: any) => {
                            const dayDate = new Date(day.date);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            dayDate.setHours(0, 0, 0, 0);
                            return dayDate <= today;
                          })
                          .sort((a: any, b: any) => {
                            const dateA = new Date(a.date);
                            const dateB = new Date(b.date);
                            return dateB.getTime() - dateA.getTime();
                          })
                          .map((day: any) => (
                            <TableRow key={day.date} className="hover:bg-muted/50">
                              <TableCell>{formatDateYMD(day.date)}</TableCell>
                              <TableCell className="text-right">
                                {formatInteger(day.sim_cards_scanned)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatDecimal(day.boxes)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatDecimal(day.expected_incentive_gb)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatDecimal(day.allocated_gb)}
                              </TableCell>
                              <TableCell className={cn("text-right",
                                Math.abs(day.difference_gb) < 0.01 ? "" :
                                day.difference_gb > 0 ? "text-red-600" : "text-yellow-600"
                              )}>
                                {day.difference_gb >= 0 ? "+" : ""}
                                {formatDecimal(day.difference_gb)}
                              </TableCell>
                              <TableCell className={cn("text-right",
                                Math.abs(day.difference_percentage) < 0.1 ? "" :
                                day.difference_percentage > 0 ? "text-red-600" : "text-yellow-600"
                              )}>
                                {day.difference_percentage >= 0 ? "+" : ""}
                                {formatDecimal(day.difference_percentage)}%
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge
                                  variant={
                                    day.status === "matched"
                                      ? "success"
                                      : day.status === "over-allocated"
                                        ? "destructive"
                                        : day.status === "under-allocated"
                                          ? "warning"
                                          : day.status === "pending"
                                            ? "info"
                                            : "secondary"
                                  }
                                >
                                  {day.status === "matched"
                                    ? "Matched"
                                    : day.status === "over-allocated"
                                      ? "Over-Allocated"
                                      : day.status === "under-allocated"
                                        ? "Under-Allocated"
                                        : day.status === "pending"
                                          ? "Pending"
                                          : day.status === "no-sims"
                                            ? "No SIMs"
                                            : "No Data"}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setReconciliationDate(day.date);
                                    setViewMode("daily");
                                    loadReconciliation(day.date);
                                  }}
                                >
                                  View Details
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : (
                <Alert className="border-blue-200 bg-blue-50 text-blue-800">
                  <AlertDescription>
                    Select a month and click &quot;Load Monthly Summary&quot; to view
                    reconciliation data
                  </AlertDescription>
                </Alert>
              )}
            </>
          ) : reconciliationLoading ? (
            <div className="flex justify-center p-8">
              <IconLoader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : reconciliationData ? (
            <>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">
                  Summary for {reconciliationData.date ?? "—"}
                </h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Period:{" "}
                  {reconciliationData.start_date != null
                    ? formatDateYMD(reconciliationData.start_date)
                    : "—"}{" "}
                  to{" "}
                  {reconciliationData.end_date != null
                    ? formatDateYMD(reconciliationData.end_date)
                    : "—"}
                </p>
                <div className="flex flex-col md:flex-row gap-4 mt-4 flex-wrap">
                  <div className="flex-1 p-4 rounded-lg border min-w-[180px]">
                    <span className="text-xs text-muted-foreground">
                      SIM Cards Scanned
                    </span>
                    <h2 className="text-xl font-bold mt-1">
                      {formatInteger(
                        reconciliationData.totals?.sim_cards_scanned,
                      )}
                    </h2>
                  </div>
                  <div className="flex-1 p-4 rounded-lg border min-w-[180px]">
                    <span className="text-xs text-muted-foreground">
                      Boxes (SIMs/200)
                    </span>
                    <h2 className="text-xl font-bold mt-1">
                      {formatDecimal(reconciliationData.totals?.boxes)}
                    </h2>
                  </div>
                  <div className="flex-1 p-4 rounded-lg border min-w-[180px]">
                    <span className="text-xs text-muted-foreground">
                      Expected Incentive Data
                    </span>
                    <h2 className="text-xl font-bold text-primary mt-1">
                      {formatDecimal(
                        reconciliationData.totals?.expected_incentive_gb,
                      )}{" "}
                      GB
                    </h2>
                  </div>
                  <div className="flex-1 p-4 rounded-lg border min-w-[180px]">
                    <span className="text-xs text-muted-foreground">
                      Allocated Data
                    </span>
                    <h2 className="text-xl font-bold text-green-600 mt-1">
                      {formatDecimal(reconciliationData.totals?.allocated_gb)}{" "}
                      GB
                    </h2>
                  </div>
                  <div className="flex-1 p-4 rounded-lg border min-w-[180px]">
                    <span className="text-xs text-muted-foreground">
                      Difference
                    </span>
                    <h2 className={cn("text-xl font-bold mt-1",
                      Math.abs(reconciliationData.totals?.difference_gb ?? 0) < 0.01
                        ? ""
                        : (reconciliationData.totals?.difference_gb ?? 0) > 0
                          ? "text-red-600"
                          : "text-yellow-600"
                    )}>
                      {(reconciliationData.totals?.difference_gb ?? 0) >= 0
                        ? "+"
                        : ""}
                      {formatDecimal(reconciliationData.totals?.difference_gb)}{" "}
                      GB
                    </h2>
                    <span className="text-xs text-muted-foreground">
                      {(reconciliationData.totals?.difference_percentage ??
                        0) >= 0
                        ? "+"
                        : ""}
                      {formatDecimal(
                        reconciliationData.totals?.difference_percentage,
                      )}
                      %
                    </span>
                  </div>
                </div>
              </div>

              <div className="overflow-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>External ID</TableHead>
                      <TableHead>Store</TableHead>
                      <TableHead className="text-right">SIM Cards</TableHead>
                      <TableHead className="text-right">Boxes</TableHead>
                      <TableHead className="text-right">Expected (GB)</TableHead>
                      <TableHead className="text-right">Allocated (GB)</TableHead>
                      <TableHead className="text-right">Difference (GB)</TableHead>
                      <TableHead className="text-right">Difference %</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reconciliationData.reconciliation.map((item: any) => (
                      <TableRow key={item.distributor_sub_distributor_id} className="hover:bg-muted/50">
                        <TableCell>
                          {item.distributor_sub_distributor_id}
                        </TableCell>
                        <TableCell>{item.external_id || "—"}</TableCell>
                        <TableCell>{item.sub_distributor_name}</TableCell>
                        <TableCell className="text-right">
                          {formatInteger(item.sim_cards_scanned)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatDecimal(item.boxes)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatDecimal(item.expected_incentive_gb)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatDecimal(item.allocated_gb)}
                        </TableCell>
                        <TableCell className={cn("text-right",
                          Math.abs(item.difference_gb) < 0.01 ? "" :
                          item.difference_gb > 0 ? "text-red-600" : "text-yellow-600"
                        )}>
                          {item.difference_gb >= 0 ? "+" : ""}
                          {formatDecimal(item.difference_gb)}
                        </TableCell>
                        <TableCell className={cn("text-right",
                          Math.abs(item.difference_percentage) < 0.1 ? "" :
                          item.difference_percentage > 0 ? "text-red-600" : "text-yellow-600"
                        )}>
                          {item.difference_percentage >= 0 ? "+" : ""}
                          {formatDecimal(item.difference_percentage)}%
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge
                            variant={
                              item.status === "matched"
                                ? "success"
                                : item.status === "over-allocated"
                                  ? "destructive"
                                  : item.status === "under-allocated"
                                    ? "warning"
                                    : "secondary"
                            }
                          >
                            {item.status === "matched"
                              ? "Matched"
                              : item.status === "over-allocated"
                                ? "Over-Allocated"
                                : item.status === "under-allocated"
                                  ? "Under-Allocated"
                                  : "No SIMs"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {reconciliationData.reconciliation.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center">
                          <p className="text-muted-foreground py-4">
                            No data found for this date
                          </p>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <Alert className="border-blue-200 bg-blue-50 text-blue-800">
              <AlertDescription>
                Select a date and click &quot;Load Daily Details&quot; to view
                reconciliation data
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Tab 6: Retailer Commissions */}
      {currentSection === "retailer-commissions" && (
        <div className="p-6">
          <RetailerCommissionsPage />
        </div>
      )}

      {/* Tab 7: Expired Rewards */}
      {currentSection === "expired-rewards" && (
        <div className="p-6">
          <MainCard className="p-6 mb-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="flex flex-row gap-2">
                  <Button
                    variant={
                      expiredRewardsViewMode === "monthly"
                        ? "default"
                        : "outline"
                    }
                    onClick={() => setExpiredRewardsViewMode("monthly")}
                    size="sm"
                  >
                    Monthly View
                  </Button>
                  <Button
                    variant={
                      expiredRewardsViewMode === "list"
                        ? "default"
                        : "outline"
                    }
                    onClick={() => setExpiredRewardsViewMode("list")}
                    size="sm"
                  >
                    List View
                  </Button>
                </div>
                <div className="min-w-[200px]">
                  <Label>Month</Label>
                  <Input
                    type="month"
                    value={expiredRewardsSelectedMonth}
                    onChange={(e) => {
                      const newMonth = e.target.value;
                      setExpiredRewardsSelectedMonth(newMonth);
                      if (newMonth) {
                        lastLoadedExpiredMonthRef.current = null;
                        void loadExpiredRewardsMonthly(newMonth);
                      }
                    }}
                  />
                </div>
                {expiredRewardsViewMode === "monthly" ? (
                  <>
                    <Button
                      onClick={() =>
                        loadExpiredRewardsMonthly(expiredRewardsSelectedMonth)
                      }
                      disabled={
                        expiredRewardsMonthlyLoading ||
                        !expiredRewardsSelectedMonth
                      }
                    >
                      {expiredRewardsMonthlyLoading ? (
                        <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshIcon className="mr-2 h-4 w-4" />
                      )}
                      {expiredRewardsMonthlyLoading
                        ? "Loading..."
                        : "Load Monthly Summary"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() =>
                        calculateExpiredRewardsHistory(
                          false,
                          expiredRewardsSelectedMonth,
                        )
                      }
                      disabled={calculatingExpiredHistory}
                    >
                      {calculatingExpiredHistory ? (
                        <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <ProcessIcon className="mr-2 h-4 w-4" />
                      )}
                      {calculatingExpiredHistory
                        ? "Calculating..."
                        : "Calculate History"}
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() =>
                      loadExpiredRewardsMonthly(expiredRewardsSelectedMonth)
                    }
                    disabled={
                      expiredRewardsMonthlyLoading ||
                      !expiredRewardsSelectedMonth
                    }
                  >
                    {expiredRewardsMonthlyLoading ? (
                      <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshIcon className="mr-2 h-4 w-4" />
                    )}
                    {expiredRewardsMonthlyLoading
                      ? "Loading..."
                      : "Load Expired Rewards"}
                  </Button>
                )}
              </div>
            </div>
          </MainCard>

          {(expiredRewardsError ||
            expiredRewardsSuccess ||
            expiredRewardsMonthlyError ||
            calculationExpiredHistoryError ||
            calculationExpiredHistorySuccess) && (
            <div className="relative mb-6">
              <Alert
                variant={
                  expiredRewardsError ||
                  expiredRewardsMonthlyError ||
                  calculationExpiredHistoryError
                    ? "destructive"
                    : undefined
                }
                className={
                  !(expiredRewardsError ||
                  expiredRewardsMonthlyError ||
                  calculationExpiredHistoryError)
                    ? "border-green-200 bg-green-50 text-green-800"
                    : undefined
                }
              >
                <AlertDescription>
                  {expiredRewardsError ||
                    expiredRewardsMonthlyError ||
                    calculationExpiredHistoryError ||
                    expiredRewardsSuccess ||
                    calculationExpiredHistorySuccess}
                </AlertDescription>
              </Alert>
              <button
                type="button"
                className="absolute right-2 top-2 rounded-md p-1 hover:bg-muted"
                onClick={() => {
                  setExpiredRewardsError("");
                  setExpiredRewardsSuccess("");
                  setExpiredRewardsMonthlyError("");
                  setCalculationExpiredHistoryError("");
                  setCalculationExpiredHistorySuccess("");
                }}
              >
                <IconX className="h-4 w-4" />
              </button>
            </div>
          )}

          {expiredRewardsViewMode === "monthly" ? (
            <>
              {expiredRewardsMonthlyLoading || calculatingExpiredHistory ? (
                <div className="flex flex-col items-center justify-center p-8">
                  <IconLoader2 className="h-6 w-6 animate-spin" />
                  <p className="text-sm text-muted-foreground mt-4">
                    {calculatingExpiredHistory
                      ? "Calculating expired rewards history..."
                      : "Loading..."}
                  </p>
                </div>
              ) : expiredRewardsMonthlySummary ? (
                <>
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2">
                      Monthly Summary -{" "}
                      {new Date(
                        expiredRewardsMonthlySummary.year,
                        expiredRewardsMonthlySummary.month - 1,
                      ).toLocaleString("default", { month: "long" })}{" "}
                      {expiredRewardsMonthlySummary.year}
                    </h3>
                    <div className="flex flex-col md:flex-row gap-4 mt-4 flex-wrap">
                      <div className="flex-1 p-4 rounded-lg border min-w-[180px]">
                        <span className="text-xs text-muted-foreground">
                          Total Count
                        </span>
                        <h2 className="text-xl font-bold mt-1">
                          {formatInteger(
                            expiredRewardsMonthlySummary.totals.count,
                          )}
                        </h2>
                      </div>
                      <div className="flex-1 p-4 rounded-lg border min-w-[180px]">
                        <span className="text-xs text-muted-foreground">
                          Total Data Value
                        </span>
                        <h2 className="text-xl font-bold mt-1">
                          {formatDecimal(
                            expiredRewardsMonthlySummary.totals.total_value /
                              1000,
                            2,
                          )}{" "}
                          GB
                        </h2>
                      </div>
                      <div className="flex-1 p-4 rounded-lg border border-green-200 min-w-[180px]">
                        <span className="text-xs text-muted-foreground">
                          Completed Days
                        </span>
                        <h2 className="text-xl font-bold text-green-600 mt-1">
                          {formatInteger(
                            expiredRewardsMonthlySummary.totals.completed_count,
                          )}
                        </h2>
                      </div>
                      <div className="flex-1 p-4 rounded-lg border border-yellow-200 min-w-[180px]">
                        <span className="text-xs text-muted-foreground">
                          Pending Days
                        </span>
                        <h2 className="text-xl font-bold text-yellow-600 mt-1">
                          {formatInteger(
                            expiredRewardsMonthlySummary.totals.pending_count,
                          )}
                        </h2>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Count</TableHead>
                          <TableHead className="text-right">Expected (GB)</TableHead>
                          <TableHead className="text-right">Actual Written (GB)</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {expiredRewardsMonthlySummary.days
                          .filter((day: any) => {
                            const dayDate = new Date(day.date);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            dayDate.setHours(0, 0, 0, 0);
                            return dayDate <= today;
                          })
                          .sort((a: any, b: any) => {
                            const dateA = new Date(a.date);
                            const dateB = new Date(b.date);
                            return dateB.getTime() - dateA.getTime();
                          })
                          .map((day: any) => {
                            const isPending = day.status === "pending";
                            const isWritingBackDay =
                              isPending && writingBack?.startsWith(day.date);
                            const isToday =
                              new Date(day.date).toDateString() ===
                              new Date().toDateString();
                            const canWriteBack =
                              isPending && day.count > 0 && !isToday;
                            return (
                              <TableRow key={day.date} className="hover:bg-muted/50">
                                <TableCell>{formatDateYMD(day.date)}</TableCell>
                                <TableCell className="text-right">
                                  {day.count !== null
                                    ? formatInteger(day.count)
                                    : "—"}
                                </TableCell>
                                <TableCell className="text-right">
                                  {day.total_value !== null
                                    ? `${formatDecimal(day.total_value / 1000, 2)} GB`
                                    : "—"}
                                </TableCell>
                                <TableCell className="text-right">
                                  {day.actual_writeback_value !== null &&
                                  day.actual_writeback_value > 0 ? (
                                    <div className="flex items-center justify-end gap-1">
                                      <span>
                                        {formatDecimal(
                                          day.actual_writeback_value / 1000,
                                          2,
                                        )}{" "}
                                        GB
                                      </span>
                                      {day.total_value !==
                                        day.actual_writeback_value && (
                                        <Badge variant="destructive" className="text-[0.65rem] h-5">
                                          Mismatch
                                        </Badge>
                                      )}
                                    </div>
                                  ) : (
                                    "—"
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge
                                    variant={
                                      day.status === "completed"
                                        ? "success"
                                        : day.status === "pending"
                                          ? "warning"
                                          : "secondary"
                                    }
                                  >
                                    {day.status === "completed"
                                      ? "Completed"
                                      : day.status === "pending"
                                        ? "Pending"
                                        : "No Data"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  {canWriteBack ? (
                                    <Button
                                      size="sm"
                                      onClick={() =>
                                        writeBackExpiredRewards(
                                          day.date,
                                          day.spend_requirement || 99,
                                        )
                                      }
                                      disabled={isWritingBackDay}
                                    >
                                      {isWritingBackDay ? (
                                        <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                                      ) : (
                                        <ProcessIcon className="mr-2 h-4 w-4" />
                                      )}
                                      {isWritingBackDay
                                        ? "Writing..."
                                        : "Write Back"}
                                    </Button>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">
                                      —
                                    </p>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : (
                <Alert className="border-blue-200 bg-blue-50 text-blue-800">
                  <AlertDescription>
                    Select a month and click &quot;Load Monthly Summary&quot; to view
                    expired rewards history
                  </AlertDescription>
                </Alert>
              )}
            </>
          ) : (
            <>
              {expiredRewardsLoading ? (
                <div className="flex justify-center py-8">
                  <IconLoader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : expiredRewards.length === 0 ? (
                <Alert className="border-blue-200 bg-blue-50 text-blue-800">
                  <AlertDescription>
                    No expired rewards found for the selected month. Only pending
                    items (not yet written back) are shown.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="overflow-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Expire Date</TableHead>
                        <TableHead className="text-right">Spend Requirement</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                        <TableHead className="text-right">Total Value</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expiredRewards.map((row) => {
                        const formattedDate =
                          formatDateOnly(row.expire_date) || row.expire_date;
                        const writeBackKey = `${formattedDate}-${row.spend_requirement}`;
                        const isWritingBack = writingBack === writeBackKey;
                        return (
                          <TableRow key={writeBackKey} className="hover:bg-muted/50">
                            <TableCell>{formattedDate}</TableCell>
                            <TableCell className="text-right">
                              {row.spend_requirement}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatInteger(row.count)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatInteger(row.total_value)}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                size="sm"
                                onClick={() =>
                                  writeBackExpiredRewards(
                                    formattedDate,
                                    row.spend_requirement,
                                  )
                                }
                                disabled={isWritingBack}
                              >
                                {isWritingBack ? (
                                  <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <ProcessIcon className="mr-2 h-4 w-4" />
                                )}
                                {isWritingBack ? "Writing..." : "Write Back"}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Email Edit Modal */}
      <Dialog
        open={!!editingEmail}
        onOpenChange={(open) => { if (!open) closeEmailModal(); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Email Address</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email Address</Label>
              <Input
                autoFocus
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter email address"
              />
            </div>
            <Alert className="border-blue-200 bg-blue-50 text-blue-800">
              <AlertDescription>
                <strong>Note:</strong> This will update the email and set
                spar_uuid_failed to 0 (mark as processed).
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeEmailModal}
              disabled={processingFailed !== null}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={saveEmailAndProcess}
              disabled={processingFailed !== null || !newEmail.trim()}
            >
              {processingFailed !== null ? "Saving..." : "Save and Process"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Process Re-run Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onOpenChange={(open) => {
          if (!open)
            setConfirmDialog({ open: false, type: "", date: "" });
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Re-run Process</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            The{" "}
            {confirmDialog.type === "markup" ? "Markup" : "Matched"}{" "}
            process has already been run for {confirmDialog.date}. Are you sure
            you want to run it again? This may overwrite existing data.
          </DialogDescription>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setConfirmDialog({
                  open: false,
                  type: "",
                  date: "",
                })
              }
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleConfirmRun}
            >
              Re-run
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
